import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { AuthUser, postLog } from '../utils/authApi';
import { getCountryCurrency } from '../utils/countryCurrency';
import { printQueueTicket } from '../utils/queuePrinter';
const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const getToken = () => localStorage.getItem('cloudairambo_jwt') || '';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

// Postgres TIMESTAMP WITHOUT TIME ZONE comes back with no 'Z' suffix, so the
// browser's Date parser treats it as local time instead of UTC — causing
// timestamps to appear shifted by the user's UTC offset.
function parseUtc(iso: string): Date {
  if (!iso) return new Date();
  return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
}

const COUNTRY_CODES: Record<string, string> = {
  AF:'93',AL:'355',DZ:'213',AS:'1684',AD:'376',AO:'244',AI:'1264',AQ:'672',AG:'1268',AR:'54',
  AM:'374',AW:'297',AU:'61',AT:'43',AZ:'994',BS:'1242',BH:'973',BD:'880',BB:'1246',BY:'375',
  BE:'32',BZ:'501',BJ:'229',BM:'1441',BT:'975',BO:'591',BA:'387',BW:'267',BR:'55',IO:'246',
  VG:'1284',BN:'673',BG:'359',BF:'226',BI:'257',KH:'855',CM:'237',CA:'1',CV:'238',KY:'1345',
  CF:'236',TD:'235',CL:'56',CN:'86',CX:'61',CC:'61',CO:'57',KM:'269',CK:'682',CR:'506',
  HR:'385',CU:'53',CW:'599',CY:'357',CZ:'420',CD:'243',DK:'45',DJ:'253',DM:'1767',DO:'1809',
  EC:'593',EG:'20',SV:'503',GQ:'240',ER:'291',EE:'372',ET:'251',FK:'500',FO:'298',FJ:'679',
  FI:'358',FR:'33',GF:'594',PF:'689',GA:'241',GM:'220',GE:'995',DE:'49',GH:'233',GI:'350',
  GR:'30',GL:'299',GD:'1473',GP:'590',GU:'1671',GT:'502',GG:'44',GN:'224',GW:'245',GY:'592',
  HT:'509',HN:'504',HK:'852',HU:'36',IS:'354',IN:'91',ID:'62',IR:'98',IQ:'964',IE:'353',
  IM:'44',IL:'972',IT:'39',CI:'225',JM:'1876',JP:'81',JE:'44',JO:'962',KZ:'7',KE:'254',
  KI:'686',XK:'383',KW:'965',KG:'996',LA:'856',LV:'371',LB:'961',LS:'266',LR:'231',LY:'218',
  LI:'423',LT:'370',LU:'352',MO:'853',MK:'389',MG:'261',MW:'265',MY:'60',MV:'960',ML:'223',
  MT:'356',MH:'692',MQ:'596',MR:'222',MU:'230',YT:'262',MX:'52',FM:'691',MD:'373',MC:'377',
  MN:'976',ME:'382',MS:'1664',MA:'212',MZ:'258',MM:'95',NA:'264',NR:'674',NP:'977',NL:'31',
  NC:'687',NZ:'64',NI:'505',NE:'227',NG:'234',NU:'683',NF:'672',KP:'850',MP:'1670',
  NO:'47',OM:'968',PK:'92',PW:'680',PS:'970',PA:'507',PG:'675',PY:'595',PE:'51',PH:'63',
  PN:'64',PL:'48',PT:'351',PR:'1787',QA:'974',CG:'242',RE:'262',RO:'40',RU:'7',RW:'250',
  BL:'590',SH:'290',KN:'1869',LC:'1758',MF:'590',PM:'508',VC:'1784',WS:'685',SM:'378',
  ST:'239',SA:'966',SN:'221',RS:'381',SC:'248',SL:'232',SG:'65',SX:'1721',SK:'421',SI:'386',
  SB:'677',SO:'252',ZA:'27',KR:'82',SS:'211',ES:'34',LK:'94',SD:'249',SR:'597',SJ:'47',
  SZ:'268',SE:'46',CH:'41',SY:'963',TW:'886',TJ:'992',TZ:'255',TH:'66',TL:'670',TG:'228',
  TK:'690',TO:'676',TT:'1868',TN:'216',TR:'90',TM:'993',TC:'1649',TV:'688',VI:'1340',
  UG:'256',UA:'380',AE:'971',GB:'44',US:'1',UY:'598',UZ:'998',VU:'678',VA:'39',VE:'58',
  VN:'84',WF:'681',EH:'212',YE:'967',ZM:'260',ZW:'263',
};
const POPULAR_COUNTRIES = ['IN','AE','US','GB','SA','SG','AU','CA','PK','BD','MY','QA','KW','OM','BH'];

// Language display-name -> Google Translate code, shared by translateTexts,
// translateSlip, and the translatedName effect.
const LANG_NAME_TO_CODE: Record<string, string> = {
  'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te', 'Kannada': 'kn',
  'Malayalam': 'ml', 'Bengali': 'bn', 'Gujarati': 'gu', 'Marathi': 'mr', 'Punjabi': 'pa',
  'Urdu': 'ur', 'French': 'fr', 'German': 'de', 'Spanish': 'es', 'Portuguese': 'pt',
  'Russian': 'ru', 'Chinese (Simplified)': 'zh', 'Chinese (Traditional)': 'zh-TW',
  'Japanese': 'ja', 'Korean': 'ko', 'Indonesian': 'id', 'Malay': 'ms',
  'Turkish': 'tr', 'Persian': 'fa', 'Hebrew': 'iw', 'Italian': 'it',
  'Dutch': 'nl', 'Polish': 'pl', 'Ukrainian': 'uk', 'Vietnamese': 'vi',
  'Thai': 'th', 'Swahili': 'sw', 'Afrikaans': 'af', 'Albanian': 'sq',
  'Amharic': 'am', 'Armenian': 'hy', 'Azerbaijani': 'az', 'Basque': 'eu',
  'Belarusian': 'be', 'Bosnian': 'bs', 'Bulgarian': 'bg', 'Catalan': 'ca',
  'Cebuano': 'ceb', 'Chichewa': 'ny', 'Corsican': 'co', 'Croatian': 'hr',
  'Czech': 'cs', 'Danish': 'da', 'Esperanto': 'eo', 'Estonian': 'et',
  'Filipino': 'tl', 'Finnish': 'fi', 'Frisian': 'fy', 'Galician': 'gl',
  'Georgian': 'ka', 'Greek': 'el', 'Haitian Creole': 'ht', 'Hausa': 'ha',
  'Hawaiian': 'haw', 'Hmong': 'hmn', 'Hungarian': 'hu', 'Icelandic': 'is',
  'Igbo': 'ig', 'Irish': 'ga', 'Javanese': 'jw', 'Kazakh': 'kk',
  'Khmer': 'km', 'Kinyarwanda': 'rw', 'Kurdish (Kurmanji)': 'ku', 'Kyrgyz': 'ky',
  'Lao': 'lo', 'Latin': 'la', 'Latvian': 'lv', 'Lithuanian': 'lt',
  'Luxembourgish': 'lb', 'Macedonian': 'mk', 'Malagasy': 'mg', 'Maltese': 'mt',
  'Maori': 'mi', 'Mongolian': 'mn', 'Myanmar (Burmese)': 'my', 'Nepali': 'ne',
  'Norwegian': 'no', 'Odia (Oriya)': 'or', 'Pashto': 'ps', 'Romanian': 'ro',
  'Samoan': 'sm', 'Scots Gaelic': 'gd', 'Serbian': 'sr', 'Sesotho': 'st',
  'Shona': 'sn', 'Sindhi': 'sd', 'Sinhala': 'si', 'Slovak': 'sk',
  'Slovenian': 'sl', 'Somali': 'so', 'Sundanese': 'su', 'Swedish': 'sv',
  'Tajik': 'tg', 'Tatar': 'tt', 'Turkmen': 'tk', 'Uyghur': 'ug',
  'Uzbek': 'uz', 'Welsh': 'cy', 'Xhosa': 'xh', 'Yiddish': 'yi',
  'Yoruba': 'yo', 'Zulu': 'zu',
};

// Languages the backend's /transliterate endpoint supports (mirrors ITC_LANGS
// in translate.py). Names go through THIS, not /translate — phonetic
// conversion, not meaning-based translation, so "rihan" -> "रिहान" not a
// random dictionary word.
const TRANSLITERATE_LANGS = new Set([
  'hi','bn','gu','kn','ml','mr','ne','pa','sa','si','ta','te',
  'ur','ar','fa','am','ru','el','iw','th','ja','ko','zh',
]);

// Maps translate-endpoint language codes to a real Intl locale, so the
// secondary date line renders in the TARGET language's own formatting —
// not hardcoded to Arabic regardless of what's selected.
const LANG_TO_LOCALE: Record<string, string> = {
  hi: 'hi-IN', bn: 'bn-IN', gu: 'gu-IN', kn: 'kn-IN', ml: 'ml-IN',
  mr: 'mr-IN', ne: 'ne-NP', pa: 'pa-IN', ta: 'ta-IN', te: 'te-IN',
  ur: 'ur-PK', ar: 'ar-EG', fa: 'fa-IR', am: 'am-ET', ru: 'ru-RU',
  el: 'el-GR', iw: 'he-IL', th: 'th-TH', ja: 'ja-JP', ko: 'ko-KR',
  zh: 'zh-CN', fr: 'fr-FR', de: 'de-DE', es: 'es-ES', pt: 'pt-PT',
  it: 'it-IT', nl: 'nl-NL', pl: 'pl-PL', tr: 'tr-TR', vi: 'vi-VN',
  id: 'id-ID', sw: 'sw-KE',
};

// Right-to-left scripts — only these need dir="rtl".
const RTL_LANGS = new Set(['ar', 'ur', 'fa', 'iw', 'ps']);

function localizedNow(lang: string): { text: string; dir: 'rtl' | 'ltr' } {
  const locale = LANG_TO_LOCALE[lang];
  if (!locale) return { text: '', dir: 'ltr' };
  try {
    return {
      // 'long' spells the full month (e.g. "अगस्त") instead of an
      // abbreviated form like "अग॰", which reads as truncated/broken.
      text: new Date().toLocaleString(locale, { dateStyle: 'long', timeStyle: 'short' }),
      dir: RTL_LANGS.has(lang) ? 'rtl' : 'ltr',
    };
  } catch {
    return { text: '', dir: 'ltr' };
  }
}

// Root cause of "Counter" -> "to oppose" in Hindi (and similar mistakes in
// other languages): the bare English word is ambiguous, so Google Translate
// picks the wrong sense — for EVERY target language, not just one. Fix
// belongs on the SOURCE side, once, in English — not a per-language output
// dictionary. Disambiguating what we SEND to /translate makes it resolve
// correctly across all 100+ target languages automatically.
// Kept SHORT (2-3 words) on purpose — /translate returns a translation of
// whatever we send, so a long explanatory phrase becomes a long label on
// the ticket. Short-but-disambiguated phrasing keeps the sense correct
// without producing a run-on label.
const SOURCE_TEXT_OVERRIDES: Record<string, string> = {
  'Counter': 'service counter',
  'Service': 'requested service',
  'Time Slot': 'appointment time',
  'Notes': 'additional notes',
};

function disambiguatedSource(label: string): string {
  return SOURCE_TEXT_OVERRIDES[label] || label;
}
// Transliterate a free-text VALUE (service name, etc.) the same way names
// are handled — sound-based conversion, not translation. Safe for short
// ambiguous strings where /translate's auto-detect would guess wrong.
// Returns the original text unchanged if tlCode isn't a transliterable
// script or the call fails.
async function transliterateValue(text: string, tlCode: string): Promise<string> {
  if (!text || !TRANSLITERATE_LANGS.has(tlCode)) return text;
  try {
    const r = await fetch(`${BASE}/transliterate?text=${encodeURIComponent(text)}&tl=${tlCode}`);
    const d = await r.json();
    

    return d.transliterated || text;
  } catch {
    return text;
  }
}

// Real /translate call for free text where we're confident it's a genuine
// sentence (so auto-detect has enough context to work reliably) — unlike
// transliterateValue, this changes MEANING, not just script.
async function translateValue(text: string, tlCode: string): Promise<string> {
  if (!text) return text;
  try {
    const r = await fetch(`${BASE}/translate?text=${encodeURIComponent(text)}&tl=${tlCode}`);
    const d = await r.json();
    return d.translated || text;
  } catch {
    return text;
  }
}

// Decides translate vs transliterate for a free-text VALUE (not a fixed
// label). Short/name-like input (1-2 words) is too ambiguous for auto-detect
// to translate reliably, so it's transliterated (sound-preserved) instead.
// Longer text reads as an actual sentence, giving auto-detect enough
// context to translate the real meaning correctly.
async function smartTranslateValue(text: string, tlCode: string): Promise<string> {
  if (!text) return text;
  const wordCount = text.trim().split(/\s+/).length;
  // 2+ words is almost always a real phrase/sentence ("come home", "carry
  // your bag") — translate it for real meaning. Only single words stay
  // transliterated, since those are the ones ambiguous enough to be a name
  // ("amnah") rather than a common word ("urgent").
  if (wordCount >= 2) {
    return translateValue(text, tlCode);
  }
  return transliterateValue(text, tlCode);
}
function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const allCodes = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || COUNTRY_CODES[c]?.includes(search.replace('+','')))
    : [...POPULAR_COUNTRIES, ...allCodes.filter(c => !POPULAR_COUNTRIES.includes(c))];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        display:'flex', alignItems:'center', gap:4, background:'#f8fafc',
        border:'1.5px solid #cbd5e1', borderRadius:'10px 0 0 10px',
        padding:'10px 10px', cursor:'pointer', fontSize:13, fontWeight:700,
        color:'#0f172a', minWidth:72, whiteSpace:'nowrap',
      }}>
        +{COUNTRY_CODES[value]||'91'} <span style={{fontSize:9,opacity:0.5}}>▾</span>
      </button>
      {open && (
        <div onClick={e=>e.stopPropagation()} style={{
          position:'absolute', top:'100%', left:0, zIndex:9999,
          background:'#fff', border:'1px solid #e2e8f0', borderRadius:10,
          boxShadow:'0 20px 60px rgba(0,0,0,0.15)', width:200, maxHeight:260,
          overflow:'hidden', display:'flex', flexDirection:'column',
        }}>
          <div style={{padding:8}}>
            <input autoFocus placeholder="Search country or +code..." value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{width:'100%',border:'1px solid #e2e8f0',borderRadius:6,padding:'5px 8px',fontSize:12,outline:'none',boxSizing:'border-box'}} />
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.map(c => (
              <button key={c} onClick={()=>{onChange(c);setOpen(false);setSearch('');}}
                style={{
                  display:'flex',alignItems:'center',gap:8,width:'100%',
                  background:c===value?'#f0f9ff':'transparent',
                  border:'none',padding:'7px 12px',cursor:'pointer',fontSize:12,color:'#374151',textAlign:'left',
                }}>
                <span style={{fontWeight:700,color:'#6366f1',minWidth:32}}>+{COUNTRY_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function detectCountryCode(defaultC?: string | null): string {
  if (defaultC && COUNTRY_CODES[defaultC]) return defaultC;

  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && COUNTRY_CODES[cached]) return cached;

  try {
    const langRegion = navigator.language?.split('-')[1]?.toUpperCase();
    if (langRegion && COUNTRY_CODES[langRegion]) {
      sessionStorage.setItem('detected_country', langRegion);
      return langRegion;
    }
  } catch {}

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Yangon':'MM','Asia/Phnom_Penh':'KH',
    'Asia/Tokyo':'JP','Asia/Seoul':'KR','Asia/Shanghai':'CN','Asia/Hong_Kong':'HK',
    'Asia/Taipei':'TW','Asia/Kabul':'AF','Asia/Tehran':'IR','Asia/Baghdad':'IQ',
    'Asia/Beirut':'LB','Asia/Amman':'JO','Asia/Jerusalem':'IL','Asia/Tashkent':'UZ',
    'Asia/Almaty':'KZ','Asia/Baku':'AZ','Asia/Tbilisi':'GE','Asia/Yerevan':'AM',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Lisbon':'PT','Europe/Paris':'FR',
    'Europe/Berlin':'DE','Europe/Amsterdam':'NL','Europe/Brussels':'BE','Europe/Madrid':'ES',
    'Europe/Rome':'IT','Europe/Vienna':'AT','Europe/Zurich':'CH','Europe/Stockholm':'SE',
    'Europe/Oslo':'NO','Europe/Copenhagen':'DK','Europe/Helsinki':'FI','Europe/Warsaw':'PL',
    'Europe/Prague':'CZ','Europe/Budapest':'HU','Europe/Bucharest':'RO','Europe/Sofia':'BG',
    'Europe/Athens':'GR','Europe/Istanbul':'TR','Europe/Moscow':'RU','Europe/Kiev':'UA',
    'America/New_York':'US','America/Chicago':'US','America/Denver':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Vancouver':'CA','America/Sao_Paulo':'BR','America/Buenos_Aires':'AR',
    'America/Mexico_City':'MX','Australia/Sydney':'AU','Australia/Melbourne':'AU',
    'Australia/Perth':'AU','Pacific/Auckland':'NZ','Africa/Cairo':'EG','Africa/Lagos':'NG',
    'Africa/Nairobi':'KE','Africa/Johannesburg':'ZA','Africa/Casablanca':'MA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function PhoneInput({ value, onChange, inputStyle, defaultCountry }: {
  value: string;
  onChange: (v: string) => void;
  inputStyle?: React.CSSProperties;
  defaultCountry?: string;
}) {
  const defaultC = defaultCountry || detectCountryCode();
  const parsed = parsePhoneValue(value, defaultC);

  const [country, setCountry] = React.useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    return defaultC;
  });
  const [number, setNumber] = React.useState(() => parsed.nationalNumber);
  const lastEmitted = React.useRef(value);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry || detectCountryCode());
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    // Strip dial code if pasted with '+' or duplicate country prefix
    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex' }}>
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1.5px solid #e2e8f0', borderLeft: 'none',
          borderRadius: '0 8px 8px 0', padding: '7px 10px',
          fontSize: 12, outline: 'none', background: '#fff',
          fontWeight: 600, color: '#0f172a', boxSizing: 'border-box',
          ...inputStyle,
        }}
      />
    </div>
  );
}


// ── Types ─────────────────────────────────────────────────────────────────────

interface Counter {
  code: string;
  name: string;
  description: string;
  current_token: number;
  called_token_code: string | null;
  desk_url: string;
  locked?: boolean;
  outlet_id?: number | null;
}

interface QToken {
  id: number;
  token_number: number;
  token_code: string;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  counter_code?: string;
  counter_name?: string;
  service?: string;
  notes?: string;
  appointment_time?: string;
  is_walk_in: boolean;
  status: 'waiting' | 'called' | 'hold' | 'urgent' | 'done' | 'skipped' | 'cancelled' | 'no_show';
  called_at?: string;
  done_at?: string;
  date: string;
  created_at: string;
  position_in_queue?: number;
  estimated_wait_mins?: number;
}

interface QueueData {
  queue_name: string;
  customer_label: string;
  counter_label: string;
  service_label: string;
  avg_wait_mins: number;
  is_open: boolean;
  today_counter: number;
  waiting_count: number;
  urgent_count: number;
  done_count: number;
  counters: Counter[];
  tokens_today: QToken[];
  locked_count: number;
  plan_status: string;
}

// ── Toolbar styles (matches Quick Invoice & POS) ─────────────────────────────

const clinicToolbarDivider: React.CSSProperties = {
  width: 1,
  height: 24,
  alignSelf: 'center',
  background: 'rgba(255,255,255,0.25)',
  margin: '0 3px',
};

const clinicToolbarBtn = (active = false, disabled = false): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  height: 36,
  padding: '0 12px',
  borderRadius: 10,
  border: active ? '1px solid rgba(255,255,255,0.55)' : '1px solid rgba(255,255,255,0.18)',
  background: active ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.14)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  whiteSpace: 'nowrap',
  transition: 'background 0.12s ease',
});

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string; emoji: string }> = {
  waiting:   { label: 'Waiting',   color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', emoji: '⏳' },
  called:    { label: 'Called',    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', emoji: '📢' },
  hold:      { label: 'On Hold',   color: '#92400e', bg: '#fffbeb', border: '#fde68a', emoji: '⏸' },
  urgent:    { label: 'Urgent',    color: '#9a3412', bg: '#fff7ed', border: '#fed7aa', emoji: '🚨' },
  done:      { label: 'Done',      color: '#166534', bg: '#f0fdf4', border: '#bbf7d0', emoji: '✅' },
  skipped:   { label: 'Skipped',   color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', emoji: '⏭' },
  cancelled: { label: 'Cancelled', color: '#9f1239', bg: '#fff1f2', border: '#fecdd3', emoji: '❌' },
  no_show:   { label: 'No Show',   color: '#7c3aed', bg: '#faf5ff', border: '#ddd6fe', emoji: '🚫' },
};

// Placeholder only — overwritten immediately by the outlet's actual local
// date from the backend (see the today-date fetch in the outlet-change
// effect below). Never trusted for display; just avoids an empty initial state.
const today = () => new Date().toISOString().slice(0, 10);
const fmtTime = (iso?: string) => iso
  ? parseUtc(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : '—';


  async function buildSlipHtml(
  token: QToken,
  queueData: QueueData,
  translations: Record<string, string> = {},
  translatedName: string = '',
  lang: string = ''
): Promise<string> {
  const num = String(token.token_number).padStart(3, '0');

  const now = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  const trackUrl = `${window.location.origin}/queue/track?token=${token.token_code}`;
  const toArabicNumerals = (str: string) => str.replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);
  const tr = (text: string) => translations[text] || text;
  const isBilingual = Object.keys(translations).length > 0;
  const isArabic = lang === 'ar';

  let qrDataUrl = '';
  try {
    const QRCode = await import('qrcode');
    qrDataUrl = await QRCode.default.toDataURL(trackUrl, { width: 200, margin: 2, errorCorrectionLevel: 'M' });
  } catch (e) { }

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; width: 80mm; padding: 4mm 6mm; background: #fff; color: #000; font-size: 11pt; }
  .center { text-align: center; }
  .divider { border-top: 1px dashed #999; margin: 3mm 0; }
  .big { font-size: 36pt; font-weight: 900; letter-spacing: 4mm; line-height: 1; }
  .label { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.08em; color: #555; }
  .row { display: flex; justify-content: space-between; font-size: 9pt; margin-bottom: 1.5mm; }
  .badge { display: inline-block; background: #000; color: #fff; padding: 1mm 4mm; border-radius: 1mm; font-size: 8pt; font-weight: 700; margin-top: 2mm; }
  .tr { color: #6366f1; }
</style>
</head><body>
<div class="slip-wrap">
<div class="center">
  <div style="font-size:13pt;font-weight:900;letter-spacing:0.5mm">${queueData.queue_name.toUpperCase()}</div>
  ${isBilingual && tr(queueData.queue_name) !== queueData.queue_name ? `<div style="font-size:11pt;font-weight:800;color:#374151;margin-top:1mm" dir="auto">${tr(queueData.queue_name)}</div>` : ''}
  <div style="font-size:8pt;color:#555;margin-top:1mm">${now}</div>
${isBilingual && localizedNow(lang).text ? `<div style="font-size:8pt;color:#6366f1;margin-top:1mm" dir="${localizedNow(lang).dir}">${localizedNow(lang).text}</div>` : ''}
  <div class="divider"></div>
  <div class="label">TOKEN NUMBER${isBilingual ? ` / <span class="tr">${tr('TOKEN NUMBER')}</span>` : ''}</div>
  <div class="big">${num}</div>
  <div class="badge">${token.is_walk_in ? 'WALK-IN' : 'APPOINTMENT'}${isBilingual ? ` / ${tr(token.is_walk_in ? 'WALK-IN' : 'APPOINTMENT')}` : ''}</div>
  <div class="divider"></div>
</div>
<div class="row"><span class="label">${queueData.customer_label}</span><span style="font-weight:700">${token.customer_name}</span></div>
${isBilingual && translatedName ? `<div class="row"><span class="label tr">${tr(queueData.customer_label)}</span><span style="font-weight:700" dir="auto">${translatedName}</span></div>` : ''}
${token.customer_phone ? `<div class="row"><span class="label">Phone</span><span>${token.customer_phone}</span></div>` : ''}
${isBilingual && token.customer_phone ? `<div class="row"><span class="label tr">${tr('Phone')}</span><span dir="auto">${isArabic ? toArabicNumerals(token.customer_phone) : token.customer_phone}</span></div>` : ''}
${token.counter_name ? `<div class="row"><span class="label">${queueData.counter_label}</span><span style="font-weight:700">${token.counter_name}</span></div>` : ''}
${isBilingual && token.counter_name ? `<div class="row"><span class="label tr">${tr(queueData.counter_label)}</span><span style="font-weight:700">${token.counter_name}</span></div>` : ''}
${token.service ? `<div class="row"><span class="label">${queueData.service_label}</span><span>${token.service}</span></div>` : ''}
${isBilingual && token.service ? `<div class="row"><span class="label tr">${tr(queueData.service_label)}</span><span dir="auto">${translations[`service:${token.service}`] || token.service}</span></div>` : ''}
${token.appointment_time ? `<div class="row"><span class="label">${isBilingual ? `Time Slot / <span class="tr">${tr('Time Slot')}</span>` : 'Time Slot'}</span><span style="font-weight:700">${isArabic ? toArabicNumerals(token.appointment_time) : token.appointment_time}</span></div>` : ''}
${token.notes ? `<div class="row"><span class="label">Notes</span><span style="font-size:8pt">${token.notes}</span></div>` : ''}
${isBilingual && token.notes ? `<div class="row"><span class="label tr">${tr('Notes')}</span><span style="font-size:8pt" dir="auto">${translations[`notes:${token.notes}`] || token.notes}</span></div>` : ''}
<div class="divider"></div>
<div class="center">
  <div style="font-size:7pt;text-transform:uppercase;letter-spacing:0.08em;color:#555;margin-bottom:2mm">Scan to track your turn${isBilingual ? ` / <span class="tr">${tr('SCAN TO TRACK YOUR TURN LIVE')}</span>` : ''}</div>
  ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:120px;height:120px;" />` : `<div style="width:120px;height:120px;border:1px dashed #ccc;display:inline-flex;align-items:center;justify-content:center;font-size:9pt;color:#999">No QR</div>`}
  <div style="font-size:7pt;color:#999;margin-top:2mm;word-break:break-all;padding:0 4mm">${trackUrl}</div>
  <div class="divider"></div>
  <div style="font-size:8pt;color:#555;line-height:1.6">You will be notified when your turn arrives.</div><div style="font-size:9pt;color:#000;font-weight:700;line-height:1.6">You will be notified when your turn arrives.</div>
  ${isBilingual ? `<div style="font-size:8pt;color:#6366f1;line-height:1.6;margin-top:1mm" dir="auto">${tr('You will receive an alarm on your phone when your turn arrives. Keep this slip.')}</div>` : ''}
  <div style="font-size:7pt;color:#bbb;margin-top:2mm;font-family:monospace">Ref: ${token.token_code}</div>
</div>
</div>
</body></html>`;
}
// ── Print slip ────────────────────────────────────────────────────────────────

async function printSlip(token: QToken, queueData: QueueData, template: 'thermal' | 'card' | 'classic' | 'print' = 'thermal', translations: Record<string, string> = {}, translatedName: string = '', lang: string = '') {
  const trackUrl = `${window.location.origin}/queue/track?token=${token.token_code}`;
  let qrDataUrl = '';
  try {
    const QRCode = await import('qrcode');
    qrDataUrl = await QRCode.default.toDataURL(trackUrl, { width: 200, margin: 2, errorCorrectionLevel: 'M' });
  } catch (e) { }


  const rawNum = String(token.token_number).padStart(3, '0');
  const num = rawNum;
  const toArabicNumerals = (str: string) => str.replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);
  const isArabic = Object.keys(translations).length > 0 && !!(translations['WALK-IN'] || '').match(/[\u0600-\u06FF]/);
  const isBilingual = Object.keys(translations).length > 0;
  const displayNum = isArabic ? toArabicNumerals(rawNum) : rawNum;
  const enNow = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  const langNow = localizedNow(lang);
  const arNow = langNow.text; // kept name for minimal diff below; now generic, not Arabic-only
  const now = enNow;
  const displayPhone = isArabic && token.customer_phone ? toArabicNumerals(token.customer_phone) : (token.customer_phone || '');
  const queueName = queueData.queue_name;
  const cl = queueData.customer_label;
  const cntl = queueData.counter_label;
  const sl = queueData.service_label;
  const tr = (text: string) => translations[text] || text;
  const tCl = tr(cl); const tCntl = tr(cntl); const tSl = tr(sl);
  const tWalkin = tr('Walk-in'); const tAppt = tr('Appointment');
  const tPhone = tr('Phone'); const tTimeSlot = tr('Time Slot');
  const tNotes = tr('Notes'); const tScan = tr('Scan to track your position');
  const tAlarm = tr('You will receive an alarm on your phone when your turn arrives. Keep this slip.');
  const displayName = token.customer_name;

  const templates: Record<string, string> = {
    thermal: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Token #${num}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Courier New',monospace;width:302px;padding:12px;background:#fff;color:#000;}
.center{text-align:center;}.divider{border-top:1px dashed #999;margin:8px 0;}
.big{font-size:64px;font-weight:900;letter-spacing:8px;color:#000;line-height:1;}
.label{font-size:9px;text-transform:uppercase;letter-spacing:0.1em;color:#555;}
.row{display:flex;justify-content:space-between;font-size:11px;margin-bottom:3px;}
@media print{@page{size:80mm auto;margin:0;}}</style></head><body>
<div class="center">
  <div style="font-size:15px;font-weight:900;letter-spacing:1px">${queueName.toUpperCase()}</div>
  ${isArabic ? `<div style="font-size:13px;font-weight:900;color:#374151;font-family:'Tahoma','Arial',sans-serif;margin-top:2px;direction:rtl">${tr(queueName)}</div>` : ''}
  <div style="font-size:10px;color:#555;margin-top:2px">${now}${arNow ? ` / ${arNow}` : ''}</div>
  <div class="divider"></div>
  <div class="label">TOKEN NUMBER${isArabic ? ` / <span style="font-family:'Tahoma','Arial',sans-serif">${tr('TOKEN NUMBER')}</span>` : ''}</div>

  <div style="display:inline-block;background:#000;color:#fff;padding:3px 14px;border-radius:3px;font-size:10px;font-weight:700;margin-top:6px;letter-spacing:1px">WALK-IN / APPOINTMENT${isArabic ? `<span style="font-family:'Tahoma','Arial',sans-serif;margin-right:6px"> / ${token.is_walk_in ? tWalkin : tAppt}</span>` : ''}</div>
  <div class="divider"></div>
</div>
<div class="row"><span class="label">${tCl}</span><span style="font-weight:700;font-size:12px">${token.customer_name}</span></div>
${isArabic ? `<div class="row"><span class="label" style="color:#6366f1;font-family:'Arial',sans-serif">${tr(cl)}</span><span style="font-weight:700;font-size:12px;color:#374151;direction:rtl;font-family:'Arial Unicode MS','Arial',sans-serif">${translatedName || token.customer_name}</span></div>` : ''}
${token.customer_phone ? `<div class="row"><span class="label">${tPhone}</span><span>${token.customer_phone}</span></div>${isArabic ? `<div class="row"><span class="label" style="color:#6366f1;font-family:'Arial',sans-serif">${tr('Phone')}</span><span style="direction:rtl;font-family:'Arial',sans-serif">${toArabicNumerals(token.customer_phone)}</span></div>` : ''}` : ''}
${token.counter_name ? `<div class="row"><span class="label">${tCntl}</span><span style="font-weight:700">${token.counter_name}</span></div>` : ''}
${token.service ? `<div class="row"><span class="label">${tSl}</span><span>${token.service}</span></div>` : ''}
${token.appointment_time ? `<div class="row"><span class="label">${tTimeSlot}</span><span style="font-weight:700">${token.appointment_time}</span></div>` : ''}
${token.notes ? `<div class="row"><span class="label">${tNotes}</span><span style="font-size:10px;max-width:160px;text-align:right">${token.notes}</span></div>` : ''}
<div class="divider"></div>
<div class="center">
  <div class="label" style="margin-bottom:6px">SCAN TO TRACK YOUR TURN LIVE${isArabic ? `<div style="font-family:'Tahoma','Arial',sans-serif;direction:rtl;margin-top:2px">${tr('SCAN TO TRACK YOUR TURN LIVE')}</div>` : ''}</div>
  ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:130px;height:130px;" />` : ''}
  <div style="font-size:7px;color:#999;word-break:break-all;margin-top:4px;padding:0 4px">${trackUrl}</div>
  <div class="divider"></div>
  <div style="font-size:10px;color:#333;line-height:1.6">${tAlarm}</div>
  <div style="font-size:8px;color:#bbb;margin-top:6px">Ref: ${token.token_code}</div>
</div></body></html>`,

    card: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Token #${num}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Inter',system-ui,sans-serif;width:340px;background:#fff;padding:20px;}
@media print{@page{size:90mm 140mm;margin:0;}body{padding:14px;}}</style></head><body>
<div style="border:2px solid #0f172a;border-radius:12px;overflow:hidden;">
  <div style="background:#0f172a;color:#fff;padding:14px 18px;display:flex;justify-content:space-between;align-items:center;">
    <div>
      <div style="font-size:13px;font-weight:900;letter-spacing:0.5px">${queueName}</div>
      ${isBilingual && tr(queueName) !== queueName ? `<div style="font-size:11px;font-weight:700;color:#a5b4fc;margin-top:1px" dir="auto">${tr(queueName)}</div>` : ''}
      <div style="font-size:9px;color:#94a3b8;margin-top:2px">${now}</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px">Token</div>
      <div style="font-size:40px;font-weight:900;letter-spacing:4px;line-height:1">${displayNum}${isArabic ? `<span style="font-size:28px;color:#6366f1;margin-left:8px;direction:rtl"> / ${toArabicNumerals(rawNum)}</span>` : ''}</div>
    </div>
  </div>
  <div style="padding:14px 18px;border-bottom:1px dashed #e2e8f0;">
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
      <div>
        <div style="font-size:8px;text-transform:uppercase;color:#94a3b8;font-weight:700;letter-spacing:0.08em">${tCl}</div>
        <div style="font-size:13px;font-weight:800;color:#0f172a;margin-top:2px">${token.customer_name}</div>
        ${isArabic && translatedName ? `<div style="font-size:12px;font-weight:700;color:#6366f1;margin-top:1px" dir="auto">${translatedName}</div>` : ''}
        ${token.customer_phone ? `<div style="font-size:10px;color:#64748b;margin-top:2px">📱 ${token.customer_phone}</div>` : ''}
        ${isArabic && token.customer_phone ? `<div style="font-size:9px;color:#6366f1;margin-top:1px" dir="auto">📱 ${toArabicNumerals(token.customer_phone)}</div>` : ''}
      </div>
      <div>
        ${token.counter_name ? `<div style="font-size:8px;text-transform:uppercase;color:#94a3b8;font-weight:700;letter-spacing:0.08em">${tCntl}</div><div style="font-size:13px;font-weight:800;color:#0f172a;margin-top:2px">${token.counter_name}</div>` : ''}
        ${token.service ? `<div style="font-size:10px;color:#64748b;margin-top:4px">🔧 ${token.service}</div>` : ''}
        ${isBilingual && token.service && translations[`service:${token.service}`] ? `<div style="font-size:9px;color:#6366f1;margin-top:1px" dir="auto">🔧 ${translations[`service:${token.service}`]}</div>` : ''}
        ${token.appointment_time ? `<div style="font-size:10px;color:#64748b;margin-top:2px">🕐 ${token.appointment_time}</div>` : ''}
        ${isArabic && token.appointment_time ? `<div style="font-size:9px;color:#6366f1;margin-top:1px" dir="auto">🕐 ${toArabicNumerals(token.appointment_time)}</div>` : ''}
      </div>
    </div>
    ${token.notes ? `<div style="margin-top:8px;font-size:10px;color:#64748b;background:#f8fafc;padding:6px 10px;border-radius:6px;border:1px solid #e2e8f0">📝 ${token.notes}</div>` : ''}
    ${isBilingual && token.notes && translations[`notes:${token.notes}`] ? `<div style="margin-top:4px;font-size:9px;color:#6366f1;background:#eef2ff;padding:6px 10px;border-radius:6px;border:1px solid #e0e7ff" dir="auto">📝 ${translations[`notes:${token.notes}`]}</div>` : ''}
  </div>
  <div style="padding:14px 18px;display:flex;gap:14px;align-items:center;">
    ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:80px;height:80px;border:1px solid #e2e8f0;border-radius:6px;" />` : ''}
    <div style="flex:1;">
      <div style="font-size:9px;font-weight:700;text-transform:uppercase;color:#0f172a;letter-spacing:0.08em;margin-bottom:4px">📱 Track Your Turn</div>
      ${isBilingual && tScan !== 'Scan to track your position' ? `<div style="font-size:8px;font-weight:600;color:#6366f1;margin-bottom:3px" dir="auto">${tScan}</div>` : ''}
      <div style="font-size:7px;color:#94a3b8;word-break:break-all;line-height:1.4">${trackUrl}</div>
      <div style="margin-top:6px;font-size:9px;color:#64748b;line-height:1.5">🔔 Alarm fires when your turn comes.</div>
      ${isBilingual && tAlarm !== 'You will receive an alarm on your phone when your turn arrives. Keep this slip.' ? `<div style="font-size:8px;color:#6366f1;margin-top:2px" dir="auto">${tAlarm}</div>` : ''}
    </div>
  </div>
  <div style="background:#f8fafc;padding:6px 18px;text-align:center;border-top:1px solid #e2e8f0;">
    <span style="font-size:8px;color:#cbd5e1;font-family:monospace">${token.token_code} · ${token.is_walk_in ? tWalkin : tAppt}</span>
  </div>
</div></body></html>`,

    classic: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Token #${num}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Times New Roman',serif;width:302px;padding:16px;background:#fff;}
.center{text-align:center;}.divider{border-top:2px solid #000;margin:10px 0;}
@media print{@page{size:80mm auto;margin:0;}}</style></head><body>
<div class="center">
  <div style="font-size:18px;font-weight:900;text-transform:uppercase;letter-spacing:2px">${queueName}</div>
  ${isBilingual && tr(queueName) !== queueName ? `<div style="font-size:14px;font-weight:700;color:#4338ca;margin-top:2px" dir="auto">${tr(queueName)}</div>` : ''}
  <div class="divider"></div>
  <div style="font-size:10px;text-transform:uppercase;letter-spacing:3px;margin-bottom:8px">Queue Token</div>
  <div style="font-size:80px;font-weight:900;line-height:1;border:3px double #000;padding:8px 16px;display:inline-block;letter-spacing:6px">${displayNum}</div>
  ${isArabic ? `<div style="font-size:56px;font-weight:900;color:#374151;direction:rtl;margin-top:4px">${toArabicNumerals(rawNum)}</div>` : ''}
  <div style="margin-top:10px;font-size:11px;text-transform:uppercase;letter-spacing:2px">${token.is_walk_in ? '— Walk-in —' : '— Appointment —'}</div>
  <div class="divider"></div>
</div>
<table style="width:100%;font-size:11px;border-collapse:collapse;">
  <tr><td style="padding:3px 0;color:#555;width:40%">${cl}:</td><td style="font-weight:700">${token.customer_name}</td></tr>
  ${isBilingual && tr(cl) !== cl ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tCl}:</td><td style="font-size:9px;color:#374151;font-weight:700" dir="auto">${translatedName || ''}</td></tr>` : ''}
  ${token.customer_phone ? `<tr><td style="padding:3px 0;color:#555">Phone:</td><td>${token.customer_phone}</td></tr>` : ''}
  ${isBilingual && token.customer_phone && tr('Phone') !== 'Phone' ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tPhone}:</td><td style="font-size:9px;color:#374151;font-weight:700" dir="auto">${isArabic ? toArabicNumerals(token.customer_phone) : token.customer_phone}</td></tr>` : ''}
  ${token.counter_name ? `<tr><td style="padding:3px 0;color:#555">${cntl}:</td><td style="font-weight:700">${token.counter_name}</td></tr>` : ''}
  ${isBilingual && token.counter_name && tr(cntl) !== cntl ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tCntl}:</td><td style="font-size:9px;color:#374151;font-weight:700">${token.counter_name}</td></tr>` : ''}
  ${token.service ? `<tr><td style="padding:3px 0;color:#555">${sl}:</td><td>${token.service}</td></tr>` : ''}
  ${isBilingual && token.service && translations[`service:${token.service}`] ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tSl}:</td><td style="font-size:9px;color:#374151;font-weight:700" dir="auto">${translations[`service:${token.service}`]}</td></tr>` : ''}
  ${token.appointment_time ? `<tr><td style="padding:3px 0;color:#555">Time:</td><td style="font-weight:700">${token.appointment_time}</td></tr>` : ''}
  ${isBilingual && token.appointment_time && tr('Time Slot') !== 'Time Slot' ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tTimeSlot}:</td><td style="font-size:9px;color:#374151;font-weight:700" dir="auto">${isArabic ? toArabicNumerals(token.appointment_time) : token.appointment_time}</td></tr>` : ''}
  ${token.notes ? `<tr><td style="padding:3px 0;color:#555">Notes:</td><td style="font-size:10px">${token.notes}</td></tr>` : ''}
  ${isBilingual && token.notes && translations[`notes:${token.notes}`] ? `<tr><td style="padding:1px 0 3px;color:#6366f1;font-size:9px" dir="auto">${tNotes}:</td><td style="font-size:9px;color:#374151;font-weight:700" dir="auto">${translations[`notes:${token.notes}`]}</td></tr>` : ''}
  <tr><td style="padding:3px 0;color:#555">Date:</td><td>${now}</td></tr>
</table>
<div class="divider"></div>
<div class="center">
  <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">Scan to track your position</div>
  ${isBilingual && tScan !== 'Scan to track your position' ? `<div style="font-size:9px;color:#4338ca;margin-bottom:6px" dir="auto">${tScan}</div>` : ''}
  ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:120px;height:120px;" />` : ''}
  <div style="border-top:1px solid #000;margin:8px 0"></div>
  <div style="font-size:9px;color:#555;line-height:1.6">${tAlarm}</div>
  <div style="font-size:7px;color:#bbb;margin-top:6px;font-family:monospace">${token.token_code}</div>
</div></body></html>`,

    print: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Token #${rawNum}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Courier New',monospace;background:#fff;width:302px;padding:12px;color:#000;}
.header{text-align:center;border-bottom:1px dashed #999;padding-bottom:8px;margin-bottom:8px;}
.token-box{border:2px solid #0f172a;border-radius:8px;padding:12px;text-align:center;margin-bottom:10px;}
.en{color:#0f172a;} .ar{color:#6366f1;direction:rtl;font-family:'Tahoma','Arial',sans-serif;}
.row{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #f1f5f9;font-size:11px;}
.label-col{color:#64748b;font-size:9px;text-transform:uppercase;letter-spacing:0.05em;}
.value-col{font-weight:700;text-align:right;}
.section{margin-bottom:10px;}
@media print{@page{size:80mm auto;margin:0;}body{width:80mm;padding:8px;}}
</style></head><body>
<div class="header">
  <div style="font-size:15px;font-weight:900;letter-spacing:1px" class="en">${queueName.toUpperCase()}</div>
  ${isArabic ? `<div style="font-size:18px;font-weight:900;margin-top:4px" class="ar">${tr(queueName)}</div>` : ''}
  <div style="font-size:11px;color:#64748b;margin-top:6px">${now}${arNow ? ` / ${arNow}` : ''}</div>
</div>
<div class="token-box">
  <div style="font-size:11px;text-transform:uppercase;letter-spacing:2px;color:#64748b">TOKEN NUMBER${isArabic ? ` / <span class="ar" style="font-size:11px">${tr('TOKEN NUMBER')}</span>` : ''}</div>
  <div style="font-size:64px;font-weight:900;letter-spacing:8px;color:#0f172a;line-height:1.1">${rawNum}</div>
  ${isArabic ? `<div style="font-size:48px;font-weight:900;letter-spacing:6px;line-height:1.1" class="ar">${toArabicNumerals(rawNum)}</div>` : ''}
  <div style="margin-top:12px;">
    <span style="background:#0f172a;color:#fff;padding:6px 20px;border-radius:6px;font-size:13px;font-weight:700;letter-spacing:1px">
      ${token.is_walk_in ? 'WALK-IN' : 'APPOINTMENT'}${isArabic ? ` / <span class="ar">${token.is_walk_in ? tWalkin : tAppt}</span>` : ''}
    </span>
  </div>
</div>
<div class="section">
  <div class="row">
    <div class="label-col"><div class="en">${cl}</div>${isArabic ? `<div class="ar" style="font-size:10px">${tCl}</div>` : ''}</div>
    <div class="value-col"><div class="en">${token.customer_name}</div>${translatedName ? `<div class="ar" style="font-size:12px">${translatedName}</div>` : ''}</div>
  </div>
  ${token.customer_phone ? `<div class="row"><div class="label-col"><div class="en">Phone</div>${isArabic ? `<div class="ar" style="font-size:10px">${tPhone}</div>` : ''}</div><div class="value-col"><div class="en">${token.customer_phone}</div>${isArabic ? `<div class="ar">${toArabicNumerals(token.customer_phone)}</div>` : ''}</div></div>` : ''}
  ${token.counter_name ? `<div class="row"><div class="label-col"><div class="en">${cntl}</div>${isArabic ? `<div class="ar" style="font-size:10px">${tCntl}</div>` : ''}</div><div class="value-col"><div class="en">${token.counter_name}</div></div></div>` : ''}
  ${token.service ? `<div class="row"><div class="label-col"><div class="en">${sl}</div>${isArabic ? `<div class="ar" style="font-size:10px">${tSl}</div>` : ''}</div><div class="value-col"><div class="en">${token.service}</div></div></div>` : ''}
  ${token.appointment_time ? `<div class="row"><div class="label-col"><div class="en">Time Slot</div>${isArabic ? `<div class="ar" style="font-size:10px">${tTimeSlot}</div>` : ''}</div><div class="value-col"><div class="en">${token.appointment_time}</div>${isArabic ? `<div class="ar">${toArabicNumerals(token.appointment_time)}</div>` : ''}</div></div>` : ''}
  ${token.notes ? `<div class="row"><div class="label-col"><div class="en">Notes</div>${isArabic ? `<div class="ar" style="font-size:10px">${tNotes}</div>` : ''}</div><div class="value-col"><div class="en" style="max-width:300px;text-align:right">${token.notes}</div></div></div>` : ''}
</div>
<div style="border:2px solid #e2e8f0;border-radius:10px;padding:16px;display:flex;gap:20px;align-items:center;">
  ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:100px;height:100px;border-radius:6px;flex-shrink:0;" />` : ''}
  <div>
    <div style="font-size:12px;font-weight:700;text-transform:uppercase;color:#0f172a;margin-bottom:4px">📱 Scan to Track Your Turn Live</div>
    ${isArabic ? `<div class="ar" style="font-size:11px;margin-bottom:6px">${tr('SCAN TO TRACK YOUR TURN LIVE')}</div>` : ''}
    <div style="font-size:9px;color:#94a3b8;word-break:break-all">${trackUrl}</div>
    <div style="font-size:11px;color:#64748b;margin-top:6px">${tAlarm}</div>
  </div>
</div>
<div style="text-align:center;margin-top:16px;font-size:9px;color:#cbd5e1;font-family:monospace">Ref: ${token.token_code}</div>
</body></html>`,
  };

  if (template === 'print') {
  const success = await printQueueTicket({
    queueName: queueData.queue_name,
    tokenNumber: token.token_number,
    tokenCode: token.token_code,

    customerName: token.customer_name,
    customerPhone: token.customer_phone,

    counterName: token.counter_name,
    service: token.service,

    appointmentTime: token.appointment_time,

    walkIn: token.is_walk_in,

    trackingUrl: trackUrl,

    reference: token.token_code,

    notes: token.notes,

    translations,
    translatedName,
    translatedService: token.service ? translations[`service:${token.service}`] : undefined,
    translatedNotes: token.notes ? translations[`notes:${token.notes}`] : undefined,
    localizedDate: localizedNow(lang).text || undefined,
    lang,

    customerLabel: queueData.customer_label,
    counterLabel: queueData.counter_label,
    serviceLabel: queueData.service_label,
  });

  if (!success) {
    console.error('Queue printer failed');
  }

  return;
}

const html = templates[template];

const printFrame = document.createElement('iframe');

printFrame.style.cssText =
  'position:fixed;inset:0;width:100%;height:100%;border:none;z-index:9999;background:#fff;';

document.body.appendChild(printFrame);

const doc =
  printFrame.contentDocument ||
  printFrame.contentWindow?.document;

if (!doc) {
  document.body.removeChild(printFrame);
  return;
}

doc.open();
doc.write(html);
doc.close();

setTimeout(() => {
  printFrame.contentWindow?.focus();
  printFrame.contentWindow?.print();

  setTimeout(() => {
    if (document.body.contains(printFrame)) {
      document.body.removeChild(printFrame);
    }
  }, 1000);
}, 600);
}

// ── Modal Wrap ────────────────────────────────────────────────────────────────

function ModalWrap({ title, onClose, children, width = 520 }: {
  title: string; onClose: () => void; children: React.ReactNode; width?: number;
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: width, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{title}</span>
          <button
  onClick={onClose}
  style={{
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    color: '#64748b',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Arial, sans-serif',
  }}
  aria-label="Close"
>
  ×
</button>
        </div>
        <div style={{ padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────

function Badge({ status }: { status: string }) {
  const m = STATUS_META[status] || STATUS_META.waiting;
  return (
    <span style={{
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>{m.emoji} {m.label}</span>
  );
}

// ── Token Card ────────────────────────────────────────────────────────────────

function TokenCard({ token, onAction, counterLabel, onPrint, onWA, onEmail, onEdit, onDelete, canSendWA, canSendEmail, canEditToken, canDeleteToken }: {
  token: QToken;
  onAction: (id: number, action: string) => void;
  counterLabel: string;
  onPrint?: (token: QToken, type: 'thermal' | 'card' | 'classic' | 'print') => void;
  onWA?: (token: QToken) => void;
  onEmail?: (token: QToken) => void;
  onEdit?: (token: QToken) => void;
  onDelete?: (token: QToken) => void;
  canSendWA?: boolean;
  canSendEmail?: boolean;
  canEditToken?: boolean;
  canDeleteToken?: boolean;
}) {
  const m = STATUS_META[token.status] || STATUS_META.waiting;
  const isCalled = token.status === 'called';
  const isUrgent = token.status === 'urgent';
  const isActive = ['waiting', 'hold', 'urgent', 'called'].includes(token.status);
  const isDim = ['done', 'skipped', 'cancelled', 'no_show'].includes(token.status);

  const Btn = ({ onClick, bg, border, color, emoji, label }: {
    onClick: () => void; bg: string; border: string; color: string; emoji: string; label: string;
  }) => (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backgroundColor: bg, border: `1.5px solid ${border}`, borderRadius: 10,
      padding: '6px 10px', minWidth: 52, gap: 2, cursor: 'pointer', flexShrink: 0,
    }}>
      <span style={{ fontSize: 15 }}>{emoji}</span>
      <span style={{ fontSize: 9, fontWeight: 700, color }}>{label}</span>
    </button>
  );

  return (
    <div style={{
      backgroundColor: isCalled ? '#f0fdf4' : isUrgent ? '#fff7ed' : '#fff',
      border: `1.5px solid ${isCalled ? '#16a34a' : isUrgent ? '#f97316' : '#e2e8f0'}`,
      borderRadius: 12, padding: 12,
      opacity: isDim ? 0.55 : 1,
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          backgroundColor: isUrgent ? '#dc2626' : isCalled ? '#16a34a' : token.status === 'hold' ? '#fef3c7' : '#6366f1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 900,
          color: token.status === 'hold' ? '#92400e' : '#fff',
        }}>
          {String(token.token_number).padStart(3, '0')}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{token.customer_name}</span>
            <span style={{
              backgroundColor: m.bg, color: m.color, border: `1px solid ${m.border}`,
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>{m.emoji} {m.label}</span>
          </div>

          {/* Meta chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {token.counter_name && (
              <span style={{ backgroundColor: '#f1f5f9', borderRadius: 6, padding: '2px 6px', fontSize: 10, color: '#475569', fontWeight: 600 }}>
                📍 {token.counter_name}
              </span>
            )}
            {token.service && (
              <span style={{ backgroundColor: '#f1f5f9', borderRadius: 6, padding: '2px 6px', fontSize: 10, color: '#475569', fontWeight: 600 }}>
                🔧 {token.service}
              </span>
            )}
            {token.appointment_time && (
              <span style={{ backgroundColor: '#eef2ff', borderRadius: 6, padding: '2px 6px', fontSize: 10, color: '#4f46e5', fontWeight: 600 }}>
                🕐 {token.appointment_time}
              </span>
            )}
            {token.customer_phone && (
              <span style={{ backgroundColor: '#f1f5f9', borderRadius: 6, padding: '2px 6px', fontSize: 10, color: '#475569', fontWeight: 600 }}>
                📱 {token.customer_phone}
              </span>
            )}
            {token.customer_email && (
              <span style={{ backgroundColor: '#eff6ff', borderRadius: 6, padding: '2px 6px', fontSize: 10, color: '#1d4ed8', fontWeight: 600 }}>
                ✉️ {token.customer_email}
              </span>
            )}
          </div>

          {isCalled && token.called_at && (
            <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700, marginTop: 4 }}>
              📢 Called at {fmtTime(token.called_at)}
            </div>
          )}
          {token.status === 'waiting' && token.position_in_queue !== undefined && (
            <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 600, marginTop: 4 }}>
              #{token.position_in_queue} in queue · ~{token.estimated_wait_mins}min wait
            </div>
          )}
        </div>
      </div>

      {/* Bottom action row */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {isActive && (<>
          {token.status !== 'called' && (
            <Btn onClick={() => onAction(token.id, 'call')} bg="#dcfce7" border="#bbf7d0" color="#15803d" emoji="📢" label="Call" />
          )}
          {token.status !== 'urgent' && (
            <Btn onClick={() => onAction(token.id, 'urgent')} bg="#fff7ed" border="#fed7aa" color="#c2410c" emoji="🚨" label="Urgent" />
          )}
          {token.status !== 'hold' && (
            <Btn onClick={() => onAction(token.id, 'hold')} bg="#fffbeb" border="#fde68a" color="#92400e" emoji="⏸" label="Hold" />
          )}
          <Btn onClick={() => onAction(token.id, 'done')} bg="#f0fdf4" border="#bbf7d0" color="#166534" emoji="✅" label="Done" />
          <Btn onClick={() => onAction(token.id, 'skip')} bg="#f8fafc" border="#e2e8f0" color="#64748b" emoji="⏭" label="Skip" />
          <Btn onClick={() => onAction(token.id, 'no_show')} bg="#faf5ff" border="#ddd6fe" color="#7c3aed" emoji="🚫" label="No Show" />
        </>)}
        {!isActive && (
          <Btn onClick={() => onAction(token.id, 'restore')} bg="#eef2ff" border="#e0e7ff" color="#4f46e5" emoji="↩" label="Restore" />
        )}
        <Btn onClick={() => onPrint?.(token, 'thermal')} bg="#f5f3ff" border="#ddd6fe" color="#7c3aed" emoji="🧾" label="PDF" />
        <Btn onClick={() => onPrint?.(token, 'print')} bg="#f5f3ff" border="#ddd6fe" color="#7c3aed" emoji="🖨" label="Print" />
        {canSendWA && (
          <Btn onClick={() => onWA?.(token)} bg="#dcfce7" border="#bbf7d0" color="#16a34a" emoji="💬" label="WA" />
        )}
        {token.customer_email && canSendEmail && (
          <Btn onClick={() => onEmail?.(token)} bg="#eff6ff" border="#bfdbfe" color="#1d4ed8" emoji="📧" label="Email" />
        )}
        {canEditToken && (
          <Btn onClick={() => onEdit?.(token)} bg="#f8fafc" border="#e2e8f0" color="#475569" emoji="✏️" label="Edit" />
        )}
        {canDeleteToken && (
          <Btn onClick={() => onDelete?.(token)} bg="#fff5f5" border="#fecaca" color="#dc2626" emoji="🗑" label="Delete" />
        )}
      </div>
    </div>
  );
}
// ── Live Slip Preview ─────────────────────────────────────────────────────────

function LiveSlipPreview({ form, queueData, slipTemplate, slipTranslations = {}, translatedName = '' }: {
  form: {
    customer_name: string; customer_phone: string; customer_email: string;
    counter_code: string; service: string; notes: string;
    appointment_time: string; is_walk_in: boolean;
  };
  queueData: QueueData | null;
  slipTemplate: 'thermal' | 'card' | 'classic';
  slipTranslations?: Record<string, string>;
  translatedName?: string;
}) {
  const ts = (text: string) => slipTranslations[text] || text;
  const isBilingual = Object.keys(slipTranslations).length > 0;
  const toArabicNumerals = (str: string) => str.replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);
  const isArabic = isBilingual && !!(slipTranslations['WALK-IN'] || '').match(/[\u0600-\u06FF]/);
  const now = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  // NOTE: LiveSlipPreview isn't passed the actual lang code today (only
  // slipTranslations), so this still can't be fully generic here without a
  // prop change — see note below.
  const arNow = isArabic ? new Date().toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  const num = isArabic ? '---' : '---';
  const cl = queueData?.customer_label || 'Customer';
  const cntl = queueData?.counter_label || 'Counter';
  const sl = queueData?.service_label || 'Service';
  const counter = queueData?.counters.find(c => c.code === form.counter_code);

  if (slipTemplate === 'card') {
    return (
      <div style={{ border: '2px solid #0f172a', borderRadius: 12, overflow: 'hidden', fontFamily: "'Inter', 'Noto Color Emoji', 'Segoe UI Emoji', 'Apple Color Emoji', system-ui, sans-serif", fontSize: 12 }}>
        <div style={{ background: '#0f172a', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900 }}>{queueData?.queue_name || 'Queue Manager'}</div>
            {isBilingual && ts(queueData?.queue_name || 'Queue Manager') !== (queueData?.queue_name || 'Queue Manager') && (
              <div style={{ fontSize: 10, fontWeight: 700, color: '#a5b4fc', marginTop: 1, direction: 'auto' }}>{ts(queueData?.queue_name || 'Queue Manager')}</div>
            )}
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{now}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 8, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Token</div>
            <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: 4, lineHeight: 1 }}>{num}</div>
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px dashed #e2e8f0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 8, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>{cl}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{form.customer_name || '—'}</div>
              {isBilingual && translatedName && (
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginTop: 1, direction: 'auto' }}>{translatedName}</div>
              )}
              {form.customer_phone && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>📱 {form.customer_phone}</div>}
              {isBilingual && form.customer_phone && (
                <div style={{ fontSize: 9, color: '#6366f1', marginTop: 1, direction: 'auto' }}>📱 {isArabic ? toArabicNumerals(form.customer_phone) : form.customer_phone}</div>
              )}
            </div>
            <div>
              {counter && <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{counter.name}</div>}
              {form.service && <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>🔧 {form.service}</div>}
              {isBilingual && form.service && slipTranslations[`service:${form.service}`] && (
                <div style={{ fontSize: 9, color: '#6366f1', marginTop: 1, direction: 'auto' }}>🔧 {slipTranslations[`service:${form.service}`]}</div>
              )}
              {form.appointment_time && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>🕐 {form.appointment_time}</div>}
              {isBilingual && form.appointment_time && (
                <div style={{ fontSize: 9, color: '#6366f1', marginTop: 1, direction: 'auto' }}>🕐 {isArabic ? toArabicNumerals(form.appointment_time) : form.appointment_time}</div>
              )}
            </div>
          </div>
          {form.notes && <div style={{ marginTop: 8, fontSize: 10, color: '#64748b', background: '#f8fafc', padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0' }}>📝 {form.notes}</div>}
          {isBilingual && form.notes && slipTranslations[`notes:${form.notes}`] && (
            <div style={{ marginTop: 4, fontSize: 9, color: '#6366f1', background: '#eef2ff', padding: '5px 8px', borderRadius: 6, border: '1px solid #e0e7ff', direction: 'auto' }}>📝 {slipTranslations[`notes:${form.notes}`]}</div>
          )}
        </div>
        <div style={{ padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ width: 64, height: 64, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#94a3b8', flexShrink: 0 }}>QR Code</div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#0f172a', marginBottom: 3 }}>📱 Track Your Turn</div>
            {isBilingual && ts('SCAN TO TRACK YOUR TURN LIVE') !== 'SCAN TO TRACK YOUR TURN LIVE' && (
              <div style={{ fontSize: 8, fontWeight: 600, color: '#6366f1', marginBottom: 3, direction: 'auto' }}>{ts('SCAN TO TRACK YOUR TURN LIVE')}</div>
            )}
            <div style={{ fontSize: 8, color: '#94a3b8' }}>Scan to get live updates + alarm</div>
            {isBilingual && ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.') !== 'You will receive an alarm on your phone when your turn arrives. Keep this slip.' && (
              <div style={{ fontSize: 8, color: '#6366f1', marginTop: 1, direction: 'auto' }}>{ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.')}</div>
            )}
          </div>
        </div>
        <div style={{ background: '#f8fafc', padding: '5px 16px', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: 8, color: '#cbd5e1', fontFamily: 'monospace' }}>{form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT'}
          {isBilingual && ts(form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT') !== (form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT') && (
            <span style={{ marginLeft: 6, fontWeight: 600, direction: 'auto' }}>/ {ts(form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT')}</span>
          )}</span>
        </div>
      </div>
    );
  }

  if (slipTemplate === 'classic') {
    return (
      <div style={{ fontFamily: "'Times New Roman',serif", fontSize: 12, padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 2 }}>{queueData?.queue_name || 'Queue'}</div>
          {isBilingual && ts(queueData?.queue_name || 'Queue') !== (queueData?.queue_name || 'Queue') && (
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', marginTop: 2, direction: 'auto' }}>{ts(queueData?.queue_name || 'Queue')}</div>
          )}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 3 }}>Queue Token</div>
          <div style={{ fontSize: 48, fontWeight: 900, border: '3px double #000', padding: '4px 12px', display: 'inline-block', letterSpacing: 6, marginTop: 4 }}>{num}</div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, marginTop: 6 }}>{form.is_walk_in ? '— Walk-in —' : '— Appointment —'}</div>
        </div>
        <div style={{ borderTop: '2px solid #000', paddingTop: 8 }}>
          {[
            [cl, form.customer_name || '—', ts(cl), translatedName || null],
            form.customer_phone ? ['Phone', form.customer_phone, ts('Phone'), isArabic ? toArabicNumerals(form.customer_phone) : null] : null,
            counter ? [cntl, counter.name, ts(cntl), null] : null,
            form.service ? [sl, form.service, ts(sl), slipTranslations[`service:${form.service}`] || null] : null,
            form.appointment_time ? ['Time', form.appointment_time, ts('Time Slot'), isArabic ? toArabicNumerals(form.appointment_time) : null] : null,
          ].filter(Boolean).map((row: any, i) => (
            <div key={i} style={{ marginBottom: 3 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#555' }}>{row[0]}:</span>
                <span style={{ fontWeight: 700 }}>{row[1]}</span>
              </div>
              {isBilingual && row[2] && row[2] !== row[0] && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginTop: 1 }}>
                  <span style={{ color: '#6366f1' }}>{row[2]}:</span>
                  {row[3] && <span style={{ color: '#374151', fontWeight: 700, direction: 'auto' }}>{row[3]}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid #000', marginTop: 8, paddingTop: 8, textAlign: 'center' }}>
          <div style={{ width: 80, height: 80, background: '#f1f5f9', margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#94a3b8' }}>QR Code</div>
          {isBilingual && ts('SCAN TO TRACK YOUR TURN LIVE') !== 'SCAN TO TRACK YOUR TURN LIVE' && (
            <div style={{ fontSize: 9, fontWeight: 600, color: '#4338ca', marginBottom: 4, direction: 'auto' }}>{ts('SCAN TO TRACK YOUR TURN LIVE')}</div>
          )}
          <div style={{ fontSize: 9, color: '#555' }}>You will be notified when your turn arrives.</div>
          {isBilingual && ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.') !== 'You will receive an alarm on your phone when your turn arrives. Keep this slip.' && (
            <div style={{ fontSize: 9, color: '#4338ca', marginTop: 2, direction: 'auto' }}>{ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.')}</div>
          )}
        </div>
      </div>
    );
  }

  // thermal (default)
  return (
    <div style={{ fontFamily: "'Courier New',monospace", fontSize: 11, padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#000' }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>{(queueData?.queue_name || 'QUEUE').toUpperCase()}</div>
        {isBilingual && slipTranslations[queueData?.queue_name || ''] && (
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginTop: 2, direction: 'auto' }}>{ts(queueData?.queue_name || 'QUEUE')}</div>
        )}
        <div style={{ fontSize: 9, color: '#555', marginTop: 2 }}>{now}</div>
        {isArabic && <div style={{ fontSize: 9, color: '#6366f1', marginTop: 1, direction: 'rtl' }}>{arNow}</div>}
        <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#555' }}>TOKEN NUMBER {isBilingual && <span style={{ color: '#6366f1' }}>/ {ts('TOKEN NUMBER')}</span>}</div>
        <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: 8, lineHeight: 1 }}>
          {num}
        </div>
        {isArabic && (
          <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: 6, lineHeight: 1, color: '#374151', direction: 'rtl' }}>
            ٠٠٠
          </div>
        )}
        <div style={{ display: 'inline-block', background: '#000', color: '#fff', padding: '2px 10px', borderRadius: 3, fontSize: 9, fontWeight: 700, marginTop: 5 }}>
          {form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT'}
          {isBilingual && ts(form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT') !== (form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT') && (
            <span style={{ marginLeft: 6, fontWeight: 600, direction: 'auto' }}>/ {ts(form.is_walk_in ? 'WALK-IN' : 'APPOINTMENT')}</span>
          )}
        </div>
        <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
      </div>
      {[
        [cl, form.customer_name || '—', ts(cl), translatedName || (isArabic && form.customer_name ? form.customer_name : null)],
        form.customer_phone ? ['Phone', form.customer_phone, ts('Phone'), isArabic ? toArabicNumerals(form.customer_phone) : null] : null,
        counter ? [cntl, counter.name, ts(cntl), null] : null,
        form.service ? [sl, form.service, ts(sl), slipTranslations[`service:${form.service}`] || null] : null,
        form.appointment_time ? ['Time Slot', form.appointment_time, ts('Time Slot'), isArabic ? toArabicNumerals(form.appointment_time) : null] : null,
      ].filter(Boolean).map((row: any, i) => (
        <div key={i} style={{ marginBottom: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
            <span style={{ color: '#555', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{row[0]}</span>
            <span style={{ fontWeight: 700 }}>{row[1]}</span>
          </div>
          {isBilingual && row[2] && row[2] !== row[0] && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginTop: 1 }}>
              <span style={{ color: '#6366f1', fontSize: 7, textTransform: 'uppercase', direction: 'rtl' }}>{row[2]}</span>
              {row[3] && <span style={{ color: '#374151', fontSize: 9, fontWeight: 700, direction: 'rtl' }}>{row[3]}</span>}
            </div>
          )}
        </div>
      ))}
      <div style={{ borderTop: '1px dashed #999', margin: '6px 0' }} />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
          SCAN TO TRACK YOUR TURN
          {isBilingual && ts('SCAN TO TRACK YOUR TURN LIVE') !== 'SCAN TO TRACK YOUR TURN LIVE' && (
            <div style={{ fontSize: 8, color: '#6366f1', direction: 'auto', textTransform: 'none', marginTop: 2 }}>{ts('SCAN TO TRACK YOUR TURN LIVE')}</div>
          )}
        </div>
        <div style={{ width: 80, height: 80, background: '#f1f5f9', margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#94a3b8' }}>QR Code</div>
        <div style={{ fontSize: 8, color: '#999', marginTop: 4 }}>🔔 Alarm fires when it's your turn</div>
        {isBilingual && ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.') !== 'You will receive an alarm on your phone when your turn arrives. Keep this slip.' && (
          <div style={{ fontSize: 8, color: '#6366f1', marginTop: 2, direction: 'auto' }}>{ts('You will receive an alarm on your phone when your turn arrives. Keep this slip.')}</div>
        )}
      </div>
    </div>
  );
}
function playChime(ctx: AudioContext | null): Promise<void> {
  return new Promise((resolve) => {
    if (!ctx) { resolve(); return; }
    try {
      [523, 659, 784].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine';
        o.frequency.value = freq;
        const t = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.4, t + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
        o.start(t); o.stop(t + 0.6);
      });
      setTimeout(resolve, 700);
    } catch { resolve(); }
  });
}

// ── Browser Web Speech Fallback (Male & Female) ──
function speakWithBrowser(text: string, lang = 'en', gender: 'female' | 'male' = 'female'): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve();
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.9;

      const voices = window.speechSynthesis.getVoices();
      const matching = voices.filter(v => v.lang.toLowerCase().startsWith(lang.split('-')[0].toLowerCase()));

      const maleKeywords = ['david', 'mark', 'george', 'ravi', 'guy', 'alex', 'fred', 'male', 'madhur'];
      const femaleKeywords = ['zira', 'samantha', 'victoria', 'heera', 'aria', 'female', 'swara', 'pallavi', 'sapna'];
      const targetKeywords = gender === 'male' ? maleKeywords : femaleKeywords;

      const picked = matching.find(v => targetKeywords.some(k => v.name.toLowerCase().includes(k))) || matching[0] || voices[0];
      if (picked) utterance.voice = picked;

      let done = false;
      utterance.onend = () => { if (!done) { done = true; resolve(); } };
      utterance.onerror = () => { if (!done) { done = true; resolve(); } };
      setTimeout(() => { if (!done) { done = true; resolve(); } }, 6000);

      window.speechSynthesis.speak(utterance);
    } catch {
      resolve();
    }
  });
}
// ── Main Component ────────────────────────────────────────────────────────────

export default function ClinicView({ authUser, moduleStatuses, accounts = [], onAddAccount, onUpdateAccount, outletId }: {
  authUser: AuthUser | null;
  moduleStatuses?: any;
  accounts?: { id: string; name: string; email: string; phone: string; company?: string; address?: string }[];
  onAddAccount?: (account: any) => void;
  onUpdateAccount?: (account: any) => void;
  outletId?: number | null;
}) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [queueData, setQueueData] = useState<QueueData | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(today());

  useEffect(() => {
    fetch(`${BASE}/auth/company-profile`, { headers: authHeaders() })
      .then(r => r.json())
      .then((p: any) => {
        if (p?.name) setCompanyName(p.name);
        else if (authUser?.name) setCompanyName(authUser.name);
      })
      .catch(() => {
        if (authUser?.name) setCompanyName(authUser.name);
      });
  }, [authUser]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCounter, setFilterCounter] = useState('all');

  // Right panel form
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    counter_code: '', service: '', notes: '',
    appointment_time: '', appointment_date: '', is_walk_in: true,
  });
  const [saving, setSaving] = useState(false);
const [phoneResetKey, setPhoneResetKey] = useState(0);
const [callingCounter, setCallingCounter] = useState<string | null>(null);
  const [lastIssued, setLastIssued] = useState<QToken | null>(null);
  const [slipTemplate, setSlipTemplate] = useState<'thermal' | 'card' | 'classic'>('thermal');
  const [showSlipPreview, setShowSlipPreview] = useState(true);
  const [slipTranslating, setSlipTranslating] = useState(false);
  const [slipTranslations, setSlipTranslations] = useState<Record<string, string>>({});
  const [translatedName, setTranslatedName] = useState('');
  const [lastIssuedTranslatedName, setLastIssuedTranslatedName] = useState('');
  // Snapshot of slipTranslations at the moment of issuing — slipTranslations
  // itself gets overwritten right after issue (form reset -> translateSlip
  // re-runs with empty notes/service), so anything keyed off notes/service
  // (Notes row, Service row) goes stale by the time the Print button is
  // clicked. Live slipTranslations is fine for labels (Customer/Counter/
  // Phone/etc — those don't change on reset), just not for the two
  // free-text keys.
  const [lastIssuedSlipTranslations, setLastIssuedSlipTranslations] = useState<Record<string, string>>({});

  // Modals
  const [showSettings, setShowSettings] = useState(false);
  const [showCounters, setShowCounters] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [ttsLangs, setTtsLangs] = useState<string[]>(['en', '', '']);
  const [ttsGender, setTtsGender] = useState<'female' | 'male'>('female');
const [displayLayout, setDisplayLayout] = useState(localStorage.getItem('display_layout') || 'all');
const [displayRotate, setDisplayRotate] = useState(Number(localStorage.getItem('display_rotate')) || 8);
  const [translateLang, setTranslateLang] = useState(() => localStorage.getItem('queue_translate_lang') || '');
  const [bilingualEnabled, setBilingualEnabled] = useState(() =>
    localStorage.getItem('cloudairambo_queue_bilingual_enabled') === 'true'
  );
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<QToken[]>([]);
  const [showAppointments, setShowAppointments] = useState(false);
  const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
  const [newCounterName, setNewCounterName] = useState('');
  const [newCounterDesc, setNewCounterDesc] = useState('');
  const [newCounterOutlet, setNewCounterOutlet] = useState('');
  const [settingsForm, setSettingsForm] = useState({
    queue_name: '', customer_label: 'Customer', counter_label: 'Counter',
    service_label: 'Service', avg_wait_mins: 10, is_open: true, daily_reset: true,
  });

  const intervalRef = useRef<any>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const settingsAutoFilledRef = useRef(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const [outlets, setOutlets] = useState<{ id: number; name: string; city: string | null }[]>([]);
  const [displayToken, setDisplayToken] = useState<string>('');
  const [waConnected, setWaConnected] = useState<boolean | null>(null); // null = still checking

  useEffect(() => {
    fetch(`${BASE}/outlets?module=queue`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => Array.isArray(data) && setOutlets(data))
      .catch(() => {});
    const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
    fetch(`${BASE}/queue/settings${settingsQ}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.display_token) setDisplayToken(d.display_token);
        setTtsLangs([d.tts_lang_1 || 'en', d.tts_lang_2 || '', d.tts_lang_3 || '']);
        setTtsGender((d.tts_gender as 'female' | 'male') || 'female');
      })
      .catch(() => {});
    fetchAnnouncements();

    // Sync the date filter to the outlet's actual local date — never the
    // browser's UTC clock. Only overwrites if the user hasn't already
    // manually picked a different date for this outlet.
    const dateQ = outletId ? `?outlet_id=${outletId}` : '';
    fetch(`${BASE}/queue/today-date${dateQ}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.today_date) setViewDate(d.today_date); })
      .catch(() => {});
  }, [outletId]);

  // Check WhatsApp connection status for the active outlet, so we can warn
  // before a send fails instead of only after.
  useEffect(() => {
    let cancelled = false;
    const checkWA = () => {
      const q = outletId ? `?outlet_id=${outletId}` : '';
      fetch(`${BASE}/whatsapp/status${q}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => { if (!cancelled) setWaConnected(d.status === 'connected'); })
        .catch(() => { if (!cancelled) setWaConnected(null); });
    };
    checkWA();
    const interval = setInterval(checkWA, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [outletId]);

  // Queue permissions
  const qPerms = {
    issueToken:     (authUser as any)?.permissions?.canQueueIssueToken     ?? authUser?.role === 'owner' ?? true,
    callNext:       (authUser as any)?.permissions?.canQueueCallNext        ?? authUser?.role === 'owner' ?? true,
    updateStatus:   (authUser as any)?.permissions?.canQueueUpdateStatus    ?? authUser?.role === 'owner' ?? true,
    sendWhatsApp:   (authUser as any)?.permissions?.canQueueSendWhatsApp    ?? authUser?.role === 'owner' ?? true,
    sendEmail:      (authUser as any)?.permissions?.canQueueSendEmail       ?? authUser?.role === 'owner' ?? true,
    editToken:      (authUser as any)?.permissions?.canQueueEditToken       ?? authUser?.role === 'owner' ?? true,
    deleteToken:    (authUser as any)?.permissions?.canQueueDeleteToken     ?? authUser?.role === 'owner' ?? true,
    manageCounters: (authUser as any)?.permissions?.canQueueManageCounters  ?? authUser?.role === 'owner' ?? true,
    settings:       (authUser as any)?.permissions?.canQueueSettings        ?? authUser?.role === 'owner' ?? true,
    viewLogs:       (authUser as any)?.permissions?.canQueueViewLogs        ?? authUser?.role === 'owner' ?? true,
  };
  const isQueueOwner = authUser?.role === 'owner';
  // Owners always get everything
  if (isQueueOwner) Object.keys(qPerms).forEach(k => (qPerms as any)[k] = true);
const translateTexts = useCallback(async (lang: string, data: QueueData) => {
    if (!lang) { setTranslations({}); return; }
    setTranslating(true);
    try {
      const textsToTranslate = [
        data.queue_name,
        data.customer_label,
        data.counter_label,
        data.service_label,
        'Issue Token', 'Walk-in', 'Appointment', 'Waiting', 'Called',
        'Done', 'Urgent', 'On Hold', 'Skipped', 'No Show', 'Cancelled',
        'All Counters', 'Phone', 'Email', 'Time Slot', 'Notes',
        'Issue Token', 'Call Next', 'Restore', 'Position', 'Estimated wait',
      ];
      const unique = [...new Set(textsToTranslate)].filter(Boolean);
      const tlCode = LANG_NAME_TO_CODE[lang] || 'en';
      const results = await Promise.all(
        unique.map(async (text) => {
          try {
            const sourceText = disambiguatedSource(text);
            const r = await fetch(`${BASE}/translate?text=${encodeURIComponent(sourceText)}&tl=${tlCode}`);
            const d = await r.json();
            return [text, d.translated || text] as [string, string];
          } catch {
            return [text, text] as [string, string];
          }
        })
      );
      setTranslations(Object.fromEntries(results));
    } catch { }
    setTranslating(false);
  }, []);
  const fetchAppointments = useCallback(async () => {
  try {
    const q = outletId ? `?outlet_id=${outletId}` : '';
    const r = await fetch(`${BASE}/queue/appointments${q}`, { headers: authHeaders() });
    if (r.ok) setAppointments(await r.json());
  } catch {}
}, [outletId]);
  const fetchQueue = useCallback(async () => {
   
    try {
      const q = outletId ? `&outlet_id=${outletId}` : '';
      const r = await fetch(`${BASE}/queue/queue?date_filter=${viewDate}${q}`, { headers: authHeaders() });
      if (r.ok) {
  const raw = await r.json();

const d: QueueData = raw;







  setQueueData(d);
        if (!settingsAutoFilledRef.current) {
          settingsAutoFilledRef.current = true;
          // Auto-fill from company profile if queue name not set
          let companyName = d.queue_name;
          if (!companyName) {
            try {
              const cp = await fetch(`${BASE}/auth/company-profile`, { headers: authHeaders() });
              if (cp.ok) { const cpd = await cp.json(); companyName = cpd.name || ''; }
            } catch {}
          }
          setSettingsForm({
           queue_name: companyName,
           customer_label: d.customer_label || 'Customer',
      counter_label: d.counter_label || 'Counter', service_label: d.service_label || 'Service',
            avg_wait_mins: d.avg_wait_mins || 10, is_open: d.is_open ?? true, daily_reset: true,
          });
        }
      }
    } catch (e) {
  console.error('🚨🚨 FETCH QUEUE FAILED:', e);
}
setLoading(false);
  }, [viewDate, outletId]);

  useEffect(() => {
    fetchQueue();
    fetchAppointments();
    intervalRef.current = setInterval(() => { fetchQueue(); fetchAppointments(); }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [fetchQueue, fetchAppointments]);

  // Intentionally not calling translateTexts here — bilingualEnabled only
  // controls the printed/preview slip (translateSlip / ts()), not the page
  // chrome. `t()` below stays an identity function so headers, buttons, and
  // tabs never get translated.

  const t = (text: string) => translations[text] || text;

  // Autofill customer name/email from a saved client account when the phone matches
  useEffect(() => {
    const digits = form.customer_phone.replace(/\D/g, '');
    if (digits.length < 7) return;
    const match = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits.slice(-10));
    if (match) {
      setForm(p => ({
        ...p,
        customer_name: p.customer_name.trim() ? p.customer_name : (match.name || p.customer_name),
        customer_email: p.customer_email.trim() ? p.customer_email : (match.email || p.customer_email),
      }));
    }
  }, [form.customer_phone, accounts]);

  useEffect(() => {
    if (!form.customer_name.trim() || !translateLang || Object.keys(slipTranslations).length === 0) {
      setTranslatedName('');
      return;
    }
    const langMap: Record<string, string> = {
      'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te', 'Kannada': 'kn',
      'Malayalam': 'ml', 'Urdu': 'ur', 'Persian': 'fa', 'Hebrew': 'iw',
      'Bengali': 'bn', 'Gujarati': 'gu', 'Marathi': 'mr', 'Punjabi': 'pa',
      'French': 'fr', 'German': 'de', 'Spanish': 'es', 'Russian': 'ru',
      'Japanese': 'ja', 'Korean': 'ko', 'Chinese (Simplified)': 'zh',
    };
    const tlCode = langMap[translateLang];
    if (!tlCode) { setTranslatedName(form.customer_name); return; }

    // Latin-script targets (French, Spanish...) — a name just stays as
    // typed, nothing to convert.
    if (!TRANSLITERATE_LANGS.has(tlCode)) { setTranslatedName(form.customer_name); return; }

    const timer = setTimeout(async () => {
      try {
        // Transliteration (sound-based) — NOT /translate (meaning-based).
        // A proper name has no "meaning" to translate.
        const r = await fetch(`${BASE}/transliterate?text=${encodeURIComponent(form.customer_name)}&tl=${tlCode}`);
        const d = await r.json();
        setTranslatedName(d.transliterated || form.customer_name);
      } catch {
        setTranslatedName(form.customer_name);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.customer_name, translateLang, Object.keys(slipTranslations).length]);
  const translateSlip = useCallback(async (lang: string, data: QueueData, currentForm?: typeof form) => {
    if (!lang) { setSlipTranslations({}); return; }
    setSlipTranslating(true);
    try {
      // Find the language code from the language name
      const langMap: Record<string, string> = {
        'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te', 'Kannada': 'kn',
        'Malayalam': 'ml', 'Bengali': 'bn', 'Gujarati': 'gu', 'Marathi': 'mr', 'Punjabi': 'pa',
        'Urdu': 'ur', 'French': 'fr', 'German': 'de', 'Spanish': 'es', 'Portuguese': 'pt',
        'Russian': 'ru', 'Chinese (Simplified)': 'zh', 'Chinese (Traditional)': 'zh-TW',
        'Japanese': 'ja', 'Korean': 'ko', 'Indonesian': 'id', 'Malay': 'ms',
        'Turkish': 'tr', 'Persian': 'fa', 'Hebrew': 'iw', 'Italian': 'it',
        'Dutch': 'nl', 'Polish': 'pl', 'Ukrainian': 'uk', 'Vietnamese': 'vi',
        'Thai': 'th', 'Swahili': 'sw', 'Afrikaans': 'af', 'Albanian': 'sq',
        'Amharic': 'am', 'Armenian': 'hy', 'Azerbaijani': 'az', 'Basque': 'eu',
        'Belarusian': 'be', 'Bosnian': 'bs', 'Bulgarian': 'bg', 'Catalan': 'ca',
        'Cebuano': 'ceb', 'Chichewa': 'ny', 'Corsican': 'co', 'Croatian': 'hr',
        'Czech': 'cs', 'Danish': 'da', 'Esperanto': 'eo', 'Estonian': 'et',
        'Filipino': 'tl', 'Finnish': 'fi', 'Frisian': 'fy', 'Galician': 'gl',
        'Georgian': 'ka', 'Greek': 'el', 'Haitian Creole': 'ht', 'Hausa': 'ha',
        'Hawaiian': 'haw', 'Hmong': 'hmn', 'Hungarian': 'hu', 'Icelandic': 'is',
        'Igbo': 'ig', 'Irish': 'ga', 'Javanese': 'jw', 'Kazakh': 'kk',
        'Khmer': 'km', 'Kinyarwanda': 'rw', 'Kurdish (Kurmanji)': 'ku', 'Kyrgyz': 'ky',
        'Lao': 'lo', 'Latin': 'la', 'Latvian': 'lv', 'Lithuanian': 'lt',
        'Luxembourgish': 'lb', 'Macedonian': 'mk', 'Malagasy': 'mg', 'Maltese': 'mt',
        'Maori': 'mi', 'Mongolian': 'mn', 'Myanmar (Burmese)': 'my', 'Nepali': 'ne',
        'Norwegian': 'no', 'Odia (Oriya)': 'or', 'Pashto': 'ps', 'Romanian': 'ro',
        'Samoan': 'sm', 'Scots Gaelic': 'gd', 'Serbian': 'sr', 'Sesotho': 'st',
        'Shona': 'sn', 'Sindhi': 'sd', 'Sinhala': 'si', 'Slovak': 'sk',
        'Slovenian': 'sl', 'Somali': 'so', 'Sundanese': 'su', 'Swedish': 'sv',
        'Tajik': 'tg', 'Tatar': 'tt', 'Turkmen': 'tk', 'Uyghur': 'ug',
        'Uzbek': 'uz', 'Welsh': 'cy', 'Xhosa': 'xh', 'Yiddish': 'yi',
        'Yoruba': 'yo', 'Zulu': 'zu',
      };


      const tlCode = langMap[lang] || 'ar';

      const textsToTranslate = [
  data.queue_name,
  data.customer_label,
  data.counter_label,
  data.service_label,

  'TOKEN NUMBER',
  'WALK-IN',
  'APPOINTMENT',
  'Phone',
  'Time Slot',
  'Notes',
  'SCAN TO TRACK YOUR TURN LIVE',

  'You will be notified when your turn arrives.',
  'You will receive an alarm on your phone when your turn arrives. Keep this slip.',

  // NOTE: currentForm.service / currentForm.notes intentionally NOT
  // included here. They're free-text the customer or staff typed — not
  // fixed vocabulary — so /translate's auto-detect frequently misreads
  // short/ambiguous input (e.g. "amnah" -> "thank you"). Only labels get
  // translated; values are shown once, as typed, under the translated label.
];
      const unique = [...new Set(textsToTranslate)].filter(Boolean);

      const results = await Promise.all(
        unique.map(async (text) => {
          try {
            // Send the disambiguated phrase to the API, but keep `text`
            // (the plain English label) as the dictionary key — everything
            // downstream (tr(), row lookups) still looks up by the label.
            const sourceText = disambiguatedSource(text);
            const r = await fetch(`${BASE}/translate?text=${encodeURIComponent(sourceText)}&tl=${tlCode}`);
            const d = await r.json();
            return [text, d.translated] as [string, string];
          } catch {
            return [text, text] as [string, string];
          }
        })
      );

      const translationsMap = Object.fromEntries(results);

      // Service and Notes values: neither goes through the /translate call
      // above (that's label-only). Both are free text, so each picks
      // translate vs transliterate based on length — see smartTranslateValue.
      if (currentForm?.service) {
        const converted = await smartTranslateValue(currentForm.service, tlCode);
        if (converted !== currentForm.service) {
          translationsMap[`service:${currentForm.service}`] = converted;
        }
      }
      if (currentForm?.notes) {
        const converted = await smartTranslateValue(currentForm.notes, tlCode);
        if (converted !== currentForm.notes) {
          translationsMap[`notes:${currentForm.notes}`] = converted;
        }
      }

      setSlipTranslations(translationsMap);
    } catch { }
    setSlipTranslating(false);
  }, []);
// Auto-translate slip whenever form or lang changes  ← paste it HERE
  useEffect(() => {
    if (!translateLang || !bilingualEnabled || !queueData) {
      setSlipTranslations({});
      return;
    }
    const timer = setTimeout(() => {
      translateSlip(translateLang, queueData, form);
    }, 500);
    return () => clearTimeout(timer);
  }, [translateLang, bilingualEnabled, queueData?.queue_name, form.service, form.notes]);
  const ts = (text: string) => slipTranslations[text] || text;

  // Save/update the customer in the shared Client Accounts list whenever a token is issued
  const syncTokenToAccounts = (name: string, phone?: string, email?: string) => {
    if (!phone) return;
    const digits = phone.replace(/\D/g, '').slice(-10);
    const existing = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits);
    if (existing) {
      if (onUpdateAccount) {
        onUpdateAccount({
          ...existing,
          name: existing.name || name,
          email: existing.email || email || '',
        });
      }
    } else if (onAddAccount) {
      onAddAccount({
        name, email: email || '', phone, address: '',
        visitCount: 0, totalSpend: 0,
      });
    }
  };

  const issueToken = async () => {
    if (!form.customer_name.trim()) { nameRef.current?.focus(); return; }
    if (counters.length > 0 && !form.counter_code) { return; }

    const rawPhone = form.customer_phone ? form.customer_phone.trim() : '';
    const cleanPhone = rawPhone ? getCleanE164Phone(rawPhone) : '';

    if (rawPhone && !cleanPhone) {
      alert('Please enter a valid phone number (between 7 and 15 digits).');
      return;
    }

    setSaving(true);
    try {
      const r = await fetch(`${BASE}/queue/tokens`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          customer_name: form.customer_name.trim(),
          customer_phone: cleanPhone || undefined,
          customer_email: form.customer_email.trim() || undefined,
          counter_code: form.counter_code,
          service: form.service,
          notes: form.notes,
          appointment_time: form.appointment_time,
          appointment_date: form.appointment_date,
          is_walk_in: form.is_walk_in,
        }),
      });
      if (!r.ok) { alert(`Error: ${await r.text()}`); return; }
      const token: QToken = await r.json();
      postLog('QUEUE_TOKEN_ISSUED', `Token #${String(token.token_number).padStart(3,'0')} issued to ${token.customer_name}${token.counter_name ? ` | ${queueData?.counter_label || 'Counter'}: ${token.counter_name}` : ''}${token.service ? ` | Service: ${token.service}` : ''}${token.is_walk_in ? ' | Walk-in' : ' | Appointment'}`);
      setLastIssued(token);
      setLastIssuedTranslatedName(translatedName);
      setLastIssuedSlipTranslations(slipTranslations);
      syncTokenToAccounts(token.customer_name, token.customer_phone, token.customer_email);

      // Auto-send WA + Email after issue
      const rawNum = String(token.token_number).padStart(3, '0');
      const trackUrl = `${window.location.origin}/queue/track?token=${token.token_code}`;
      const langCodeMap: Record<string, string> = {
        'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
        'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
        'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
      };

      let pdfBase64: string | null = null;
      try {
        const html = await buildSlipHtml(token, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
        const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${rawNum}.pdf` }),
        });
        if (pdfRes.ok) { const d = await pdfRes.json(); pdfBase64 = d.base64; }
      } catch {}

   
const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';

if (token.customer_phone && qPerms.sendWhatsApp) {
        const digits = token.customer_phone.replace(/\D/g, '');
        const phone = digits.length === 10 ? `91${digits}` : digits;
        const caption = `🎫 Token: *${rawNum}*\n👤 ${token.customer_name}${token.counter_name ? `\n📍 ${queueData?.counter_label || 'Counter'}: ${token.counter_name}` : ''}\n\nTrack your turn:\n${trackUrl}\n\n🔔 Alarm fires when it's your turn!\n\n— ${activeBrand}`;
        try {
          if (pdfBase64) {
            await fetch(`${BASE}/whatsapp/send-pdf`, {
              method: 'POST', headers: authHeaders(),
              body: JSON.stringify({ phone, base64: pdfBase64, filename: `Token_${rawNum}.pdf`, caption, outlet_id: outletId }),
            });
          } else {
            await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
          }
        } catch {}
      }

      if (token.customer_email && qPerms.sendEmail) {
        const emailHtml = `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#6366f1">🎫 Token #${rawNum}</h2><p>Hi <strong>${token.customer_name}</strong>!</p>${token.counter_name ? `<p>📍 ${queueData?.counter_label || 'Counter'}: <strong>${token.counter_name}</strong></p>` : ''}${token.service ? `<p>🔧 ${token.service}</p>` : ''}<p>Your token slip is attached as a PDF.</p><a href="${trackUrl}" style="display:inline-block;margin-top:16px;padding:12px 24px;background:#6366f1;color:#fff;border-radius:10px;text-decoration:none;font-weight:700">🔔 Track Your Turn</a><p style="margin-top:20px;font-size:12px;color:#94a3b8">— ${activeBrand}</p></div>`;
        try {
          if (pdfBase64) {
            await fetch(`${BASE}/email/send`, {
              method: 'POST', headers: authHeaders(),
              body: JSON.stringify({
                to: token.customer_email,
                subject: `Your Queue Token #${rawNum} — ${activeBrand}`,
                html: emailHtml,
                attachments: [{ filename: `Token_${rawNum}.pdf`, content: pdfBase64, encoding: 'base64' }],
                outlet_id: outletId,
              }),
            });
          } else {
            await fetch(`${BASE}/email/send`, {
              method: 'POST', headers: authHeaders(),
              body: JSON.stringify({ to: token.customer_email, subject: `Your Queue Token #${rawNum} — ${activeBrand}`, html: emailHtml, outlet_id: outletId }),
            });
          }
        } catch {}
      }

     fetchQueue();
// Reset form for next patient
setForm(p => ({ ...p, customer_name: '', customer_phone: '', customer_email: '', notes: '', appointment_time: '', is_walk_in: true }));
setPhoneResetKey(k => k + 1); // force PhoneInput to remount and clear its internal number/country state
setTimeout(() => nameRef.current?.focus(), 100);
    } catch { }
    setSaving(false);
  };

  const doAction = async (tokenId: number, action: string) => {
    try {
      await fetch(`${BASE}/queue/tokens/action/${tokenId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ action }),
      });
      const token = tokens.find(t => t.id === tokenId);
      postLog('QUEUE_TOKEN_ACTION', `Token #${token ? String(token.token_number).padStart(3,'0') : tokenId} — ${action.toUpperCase()}${token ? ` | ${token.customer_name}` : ''}${token?.counter_name ? ` | ${queueData?.counter_label || 'Counter'}: ${token.counter_name}` : ''}`);
      fetchQueue();
    } catch {}
  };

  const callNext = async (counterCode: string) => {
  if (callingCounter) return;

  
  // Create/resume AudioContext on user gesture
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtxRef.current.state === 'suspended') {
    await audioCtxRef.current.resume();
  }
  
  setCallingCounter(counterCode);
  try {
    const r = await fetch(`${BASE}/queue/counters/${counterCode}/call-next`, {
      method: 'POST', headers: authHeaders(),
    });
    if (r.ok) {
      const data = await r.json();
      const num = String(data?.token_number || data?.current_token || '').padStart(3, '0');
      const name = data?.customer_name || '';
      const counter = counters.find(c => c.code === counterCode);
      const digits = num.split('').join(' ');
const toArabicNumerals = (str: string) => str.replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);
const arabicDigits = toArabicNumerals(num).split('').join(' ');

      // Get active langs, filter empty
      const langs = ttsLangs.filter(Boolean);
      if (langs.length === 0) langs.push('en');
      
    const audios: HTMLAudioElement[] = [];
    const playNext = (index: number) => {
        if (index >= langs.length) return;
        const lang = langs[index];
     const ANNOUNCEMENTS: Record<string, (d: string, c: string) => string> = {
  en: (d, c) => `Token number ${d}. Please proceed to ${c}.`,
  // Indian
  hi: (d, c) => `टोकन नंबर ${d}. कृपया ${c} पर जाएं।`,
  pa: (d, c) => `ਟੋਕਨ ਨੰਬਰ ${d}. ਕਿਰਪਾ ਕਰਕੇ ${c} ਤੇ ਜਾਓ।`,
  ur: (d, c) => `ٹوکن نمبر ${d}. براہ کرم ${c} پر جائیں۔`,
  ta: (d, c) => `டோக்கன் எண் ${d}. தயவுசெய்து ${c} க்கு செல்லவும்.`,
  te: (d, c) => `టోకెన్ నంబర్ ${d}. దయచేసి ${c} కి వెళ్ళండి.`,
  kn: (d, c) => `ಟೋಕನ್ ನಂಬರ್ ${d}. ದಯವಿಟ್ಟು ${c} ಗೆ ಹೋಗಿ.`,
  ml: (d, c) => `ടോക്കൺ നമ്പർ ${d}. ദയവായി ${c} ലേക്ക് പോകൂ.`,
  mr: (d, c) => `टोकन क्रमांक ${d}. कृपया ${c} कडे जा.`,
  gu: (d, c) => `ટોકન નંબર ${d}. કૃपा करके ${c} पर जाएं।`,
  bn: (d, c) => `টোকেন নম্বর ${d}. অনুগ্রহ করে ${c} এ যান।`,
  si: (d, c) => `ටෝකන් අංකය ${d}. කරුණාකර ${c} වෙත යන්න.`,
  ne: (d, c) => `टोकन नम्बर ${d}. कृपया ${c} मा जानुहोस्।`,
  // Middle East
  ar: (d, c) => `رقم التوكن ${d.replace(/[0-9]/g, n => '٠١٢٣٤٥٦٧٨٩'[+n])}. يرجى التوجه إلى ${c}.`,
  he: (d, c) => `מספר תור ${d}. אנא פנה אל ${c}.`,
  fa: (d, c) => `شماره نوبت ${d}. لطفاً به ${c} بروید.`,
  ps: (d, c) => `د ټوکن شمیره ${d}. مهرباني وکړئ ${c} ته لاړ شئ.`,
  // European
  fr: (d, c) => `Numéro de ticket ${d}. Veuillez vous rendre au ${c}.`,
  es: (d, c) => `Número de turno ${d}. Por favor diríjase a ${c}.`,
  de: (d, c) => `Tokennummer ${d}. Bitte gehen Sie zu ${c}.`,
  it: (d, c) => `Numero token ${d}. Si prega di recarsi a ${c}.`,
  nl: (d, c) => `Tokennummer ${d}. Gaat u alstublieft naar ${c}.`,
  pl: (d, c) => `Numer tokenu ${d}. Proszę udać się do ${c}.`,
  ru: (d, c) => `Номер талона ${d}. Пожалуйста пройдите к ${c}.`,
  uk: (d, c) => `Номер талона ${d}. Будь ласка пройдіть до ${c}.`,
  ro: (d, c) => `Numărul tokenului ${d}. Vă rugăm să mergeți la ${c}.`,
  cs: (d, c) => `Číslo tokenu ${d}. Prosím jděte k ${c}.`,
  hu: (d, c) => `Token szám ${d}. Kérjük menjen a ${c}.`,
  fi: (d, c) => `Token numero ${d}. Ole hyvä ja mene ${c}.`,
  sv: (d, c) => `Tokennummer ${d}. Vänligen gå till ${c}.`,
  no: (d, c) => `Tokennummer ${d}. Vennligst gå til ${c}.`,
  da: (d, c) => `Tokennummer ${d}. Gå venligst til ${c}.`,
  el: (d, c) => `Αριθμός token ${d}. Παρακαλώ προχωρήστε στο ${c}.`,
  pt: (d, c) => `Número do token ${d}. Por favor dirija-se a ${c}.`,
  tr: (d, c) => `Token numarası ${d}. Lütfen ${c} e gidin.`,
  bg: (d, c) => `Номер на жетона ${d}. Моля отидете до ${c}.`,
  hr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
  sk: (d, c) => `Číslo tokenu ${d}. Prosím choďte k ${c}.`,
  sl: (d, c) => `Številka žetona ${d}. Prosimo pojdite k ${c}.`,
  sr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
  lt: (d, c) => `Žetono numeris ${d}. Prašome eiti prie ${c}.`,
  lv: (d, c) => `Žetona numurs ${d}. Lūdzu dodieties uz ${c}.`,
  et: (d, c) => `Žetoni number ${d}. Palun minge ${c} juurde.`,
  is: (d, c) => `Númer tákns ${d}. Vinsamlegast farðu til ${c}.`,
  mk: (d, c) => `Број на токен ${d}. Ве молиме одете кај ${c}.`,
  sq: (d, c) => `Numri i tokenit ${d}. Ju lutemi shkoni tek ${c}.`,
  bs: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
  ca: (d, c) => `Número de torn ${d}. Si us plau aneu a ${c}.`,
  gl: (d, c) => `Número de quenda ${d}. Por favor vai a ${c}.`,
  cy: (d, c) => `Rhif tocyn ${d}. Ewch i ${c} os gwelwch yn dda.`,
  ga: (d, c) => `Uimhir comhartha ${d}. Téigh go ${c} le do thoil.`,
  mt: (d, c) => `Numru tal-token ${d}. Jekk jogħġbok mur ${c}.`,
  af: (d, c) => `Token nommer ${d}. Gaan asseblief na ${c}.`,
  // Asian
  'zh-CN': (d, c) => `号码 ${d}。请前往 ${c}。`,
  ja: (d, c) => `番号 ${d}。${c} へお越しください。`,
  ko: (d, c) => `번호 ${d}. ${c} 로 오십시오.`,
  vi: (d, c) => `Số thứ tự ${d}. Vui lòng đến ${c}.`,
  th: (d, c) => `หมายเลข ${d}. กรุณาไปที่ ${c}.`,
  id: (d, c) => `Nomor token ${d}. Silakan menuju ke ${c}.`,
  ms: (d, c) => `Nombor token ${d}. Sila pergi ke ${c}.`,
  km: (d, c) => `លេខសំបុត្រ ${d}។ សូមទៅកាន់ ${c}។`,
  lo: (d, c) => `ເລກໂທເຄັນ ${d}. ກະລຸນາໄປທີ່ ${c}.`,
  my: (d, c) => `တိုကင်နံပါတ် ${d}။ ကျေးဇူးပြု၍ ${c} သို့သွားပါ။`,
  mn: (d, c) => `Токений дугаар ${d}. ${c} рүү явна уу.`,
  jv: (d, c) => `Nomer token ${d}. Mangga menyang ${c}.`,
  su: (d, c) => `Nomer token ${d}. Punten angkat ka ${c}.`,
  fil: (d, c) => `Token numero ${d}. Mangyaring pumunta sa ${c}.`,
  ka: (d, c) => `ტოკენის ნომერი ${d}. გთხოვთ გახვიდეთ ${c}-ში.`,
  kk: (d, c) => `Токен нөмірі ${d}. ${c} жаққа өтіңіз.`,
  az: (d, c) => `Token nömrəsi ${d}. Zəhmət olmasa ${c} a keçin.`,
  uz: (d, c) => `Token raqami ${d}. Iltimos ${c} ga o'ting.`,
  // African
  sw: (d, c) => `Nambari ya token ${d}. Tafadhali nenda kwa ${c}.`,
  so: (d, c) => `Lambarka token ${d}. Fadlan aad ${c}.`,
  am: (d, c) => `የቶከን ቁጥር ${d}። እባክዎ ወደ ${c} ይሂዱ።`,
  zu: (d, c) => `Inombolo ye-token ${d}. Sicela uye ku ${c}.`,
  ha: (d, c) => `Lambar token ${d}. Don Allah je ${c}.`,
  yo: (d, c) => `Nọmba token ${d}. Jọwọ lọ si ${c}.`,
};
const fn = ANNOUNCEMENTS[lang] || ANNOUNCEMENTS['en'];
const text = fn(lang === 'ar' ? arabicDigits : digits, counter?.name || counterCode);
        const fallback = () => {
          speakWithBrowser(text, lang, ttsGender).finally(() => playNext(index + 1));
        };

        fetch(`${BASE}/queue/tts?text=${encodeURIComponent(text)}&lang=${lang}&gender=${ttsGender}`)
        .then(r => {
          if (!r.ok) throw new Error('Server TTS failed');
          return r.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audios.push(audio);
          audio.volume = 1;

          audio.onended = () => {
            URL.revokeObjectURL(url);
            audios.splice(audios.indexOf(audio), 1);
            playNext(index + 1);
          };

          audio.onerror = () => {
            URL.revokeObjectURL(url);
            audios.splice(audios.indexOf(audio), 1);
            fallback();
          };

          audio.play().catch(() => {
            URL.revokeObjectURL(url);
            audios.splice(audios.indexOf(audio), 1);
            fallback();
          });
        })
        .catch(() => {
          fallback();
        });

    };

    await playChime(audioCtxRef.current);
    playNext(0);

    postLog('QUEUE_CALL_NEXT', `Call Next triggered at ${counter?.name || counterCode}`);
    fetchQueue();
    }
  } catch { }
  setCallingCounter(null);
};


  const saveSettings = async () => {
    const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
    await fetch(`${BASE}/queue/settings${settingsQ}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify(settingsForm),
    });
    postLog('QUEUE_SETTINGS_UPDATED', `Queue settings updated | Name: ${settingsForm.queue_name} | Customer: ${settingsForm.customer_label} | Counter: ${settingsForm.counter_label} | Open: ${settingsForm.is_open}`);
    fetchQueue();
    setShowSettings(false);
  };

 const createCounter = async () => {
  if (!newCounterName.trim()) return;
  const r = await fetch(`${BASE}/queue/counters`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({
      name: newCounterName,
      description: newCounterDesc,
      outlet_id: newCounterOutlet ? Number(newCounterOutlet) : (outletId || null),
    }),
  });
  if (!r.ok) {
    const err = await r.text();
    let detail = 'Could not add counter.';
    try { detail = JSON.parse(err).detail || detail; } catch {}
    alert(`⚠️ ${detail}`);
    return;
  }
  postLog('QUEUE_COUNTER_ADDED', `Counter added: ${newCounterName}${newCounterDesc ? ` | ${newCounterDesc}` : ''}`);
  setNewCounterName(''); setNewCounterDesc(''); setNewCounterOutlet('');
  fetchQueue();
};

  const deleteCounter = async (code: string) => {
    if (!confirm('Delete this counter?')) return;
    const counter = counters.find(c => c.code === code);
    await fetch(`${BASE}/queue/counters/${code}`, { method: 'DELETE', headers: authHeaders() });
    postLog('QUEUE_COUNTER_DELETED', `Counter deleted: ${counter?.name || code}`);
    fetchQueue();
  };
const [panelWidth, setPanelWidth] = useState(460);
const [customAnnounceText, setCustomAnnounceText] = useState('');
const [customAnnounceLang, setCustomAnnounceLang] = useState('en');
const [customAnnouncing, setCustomAnnouncing] = useState(false);
const [customAnnounceGender, setCustomAnnounceGender] = useState<'female' | 'male'>('female');
const [savedAnnouncements, setSavedAnnouncements] = useState<any[]>([]);

const fetchAnnouncements = useCallback(async () => {
  try {
    const q = outletId ? `?outlet_id=${outletId}` : '';
    const r = await fetch(`${BASE}/queue/announcements${q}`, { headers: authHeaders() });
    if (r.ok) setSavedAnnouncements(await r.json());
  } catch {}
}, [outletId]);

  const tokens = queueData?.tokens_today || [];
  const counters = queueData?.counters || [];
const [showTimePicker, setShowTimePicker] = useState(false);
const [clockStep, setClockStep] = useState<'hour' | 'minute'>('hour');
const [clockHour, setClockHour] = useState(9);
const [clockMinute, setClockMinute] = useState(0);
const [clockPeriod, setClockPeriod] = useState<'AM' | 'PM'>('AM');
const [editingToken, setEditingToken] = useState<QToken | null>(null);
const customTimeInputRef = useRef<HTMLInputElement>(null);
const [editForm, setEditForm] = useState<Partial<QToken>>({});
const [editSaving, setEditSaving] = useState(false);
  const openTimePicker = () => {
    if (form.appointment_time) {
      const [hh, mm] = form.appointment_time.split(':').map(Number);
      const period: 'AM' | 'PM' = hh >= 12 ? 'PM' : 'AM';
      setClockHour(hh % 12 || 12);
      setClockMinute(mm);
      setClockPeriod(period);
    } else {
      const now = new Date();
      const period: 'AM' | 'PM' = now.getHours() >= 12 ? 'PM' : 'AM';
      setClockHour(now.getHours() % 12 || 12);
      setClockMinute(Math.round(now.getMinutes() / 5) * 5 % 60);
      setClockPeriod(period);
    }
    setClockStep('hour');
    setShowTimePicker(true);
  };

  const filtered = tokens.filter(t => {
    const statusMatch = filterStatus === 'all' || t.status === filterStatus;
    const counterMatch = filterCounter === 'all' || t.counter_code === filterCounter || (!t.counter_code && filterCounter === 'unassigned');
    return statusMatch && counterMatch;
  });

  const waitingCount = tokens.filter(t => ['waiting', 'urgent', 'hold'].includes(t.status)).length;
  const urgentCount = tokens.filter(t => t.status === 'urgent').length;
  const doneCount = tokens.filter(t => t.status === 'done').length;

  const F: React.CSSProperties = {
    padding: '10px 13px', borderRadius: 10, border: '1.5px solid #cbd5e1',
    fontSize: 14, color: '#0f172a', outline: 'none', fontFamily: 'inherit',
    width: '100%', boxSizing: 'border-box', background: '#fff',
  };
  const L: React.CSSProperties = {
    fontSize: 11, fontWeight: 800, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5,
  };

  const [queueModulePrice, setQueueModulePrice] = useState<number | null>(null);
  const [queueBillingCurrency, setQueueBillingCurrency] = useState(
    localStorage.getItem('cloudairambo_active_currency') || 'INR'
  );
  const queueCurrSym = (() => {
    const map: Record<string, string> = { INR: '₹', AED: 'AED ', USD: '$', GBP: '£', EUR: '€', SGD: 'S$', AUD: 'A$', CAD: 'C$' };
    return map[queueBillingCurrency] || queueBillingCurrency + ' ';
  })();

  useEffect(() => {
    fetch(`${BASE}/billing/my-country`)
      .then(r => r.json())
      .then(geo => {
        const country = geo.country || '';
        return fetch(`${BASE}/billing/prices?country=${country}`)
          .then(r => r.json())
          .then((rows: any[]) => {
            const countryRows = rows.filter((r: any) => r.country === country);
const activeCurr = countryRows.length ? countryRows[0].currency : getCountryCurrency(country);
setQueueBillingCurrency(activeCurr);
            const row = rows.find(r => r.plan === 'queue' && r.billing_cycle === 'monthly');
            if (row) setQueueModulePrice(row.price);
          });
      })
      .catch(() => {});
  }, []);

  const queueModuleStatus = (moduleStatuses as any)?.['queue'];
  if (queueModuleStatus === 'locked') {
    return (
      <div style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: 40, maxWidth: 400 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>Queue Access Expired</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 32, lineHeight: 1.6 }}>Your Queue subscription has ended. Renew to access your counters, tokens, and queue history.</div>
          <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
  style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px 32px', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
  Choose a Plan →
</button>
        </div>
      </div>
    );
  }

  return (
    <>
    <style>{`
      .clinic-layout {
  flex-direction: row;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.clinic-layout button,
.clinic-layout input,
.clinic-layout select,
.clinic-layout textarea {
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
      .clinic-issue-panel { width: ${panelWidth}px; }
      @media (max-width: 900px) {
        .clinic-layout {
          flex-direction: column;
          overflow-y: auto !important;
        }
        .clinic-issue-panel {
          width: 100% !important;
          max-width: 100% !important;
          border-left: none !important;
          border-top: 1px solid #e2e8f0;
        }
        .clinic-resize-handle {
          display: none !important;
        }
        .clinic-stats-grid {
          grid-template-columns: repeat(2, 1fr) !important;
        }
        .clinic-counters-grid {
          grid-template-columns: 1fr !important;
        }
      }
    `}</style>
    <div className="clinic-layout" style={{ flex: 1, display: 'flex', overflow: 'hidden', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#f8fafc' }}>

      {/* ── LEFT: Queue View ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Header */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 20px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                🎫 {t(queueData?.queue_name || 'Queue Manager')}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                {new Date().toLocaleDateString('en-IN', { dateStyle: 'long' })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <div onClick={async () => {
                if (!isQueueOwner) return;
                const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
                await fetch(`${BASE}/queue/settings${settingsQ}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ is_open: !queueData?.is_open }) });
                postLog('QUEUE_STATUS_CHANGED', `Queue ${!queueData?.is_open ? 'opened' : 'closed'} — ${queueData?.queue_name || 'Queue'}`);
                fetchQueue();
              }} style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: queueData?.is_open ? '#f0fdf4' : '#fef2f2',
                color: queueData?.is_open ? '#16a34a' : '#dc2626',
                border: `1px solid ${queueData?.is_open ? '#bbf7d0' : '#fecaca'}`,
                cursor: isQueueOwner ? 'pointer' : 'default',
                opacity: isQueueOwner ? 1 : 0.5,
              }}>
                {queueData?.is_open ? '🟢 Open' : '🔴 Closed'}
              </div>
              {qPerms.manageCounters && (
                <button onClick={() => setShowCounters(true)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                  📍 Counters
                </button>
              )}
              {qPerms.viewLogs && (
                <button onClick={async () => {
                  const r = await fetch(`${BASE}/queue/logs`, { headers: authHeaders() });
                  if (r.ok) setLogs(await r.json());
                  setShowLogs(true);
                }} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                  📋 Logs
                </button>
              )}
            
              {qPerms.settings && (
                <button onClick={() => setShowSettings(true)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                  ⚙ Settings
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
{/* WhatsApp disconnected banner */}
{qPerms.sendWhatsApp && waConnected === false && (
  <div style={{
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
    padding: '10px 14px', fontSize: 12, color: '#991b1b',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  }}>
    <span>📵 WhatsApp isn't connected for this outlet — token/queue messages won't send until it's linked.</span>
    <button onClick={() => window.dispatchEvent(new CustomEvent('open-whatsapp-settings'))}
      style={{ padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
      Connect WhatsApp
    </button>
  </div>
)}


{/* Locked counters warning */}
{(queueData?.locked_count ?? 0) > 0 && (
  <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
    <span>
      ⚠️ {queueData!.locked_count} {queueData?.counter_label || 'Counter'}{queueData!.locked_count > 1 ? 's are' : ' is'} locked — not yet subscribed individually.
      Subscribe to each locked {(queueData?.counter_label || 'counter').toLowerCase()} in Billing to unlock it.
    </span>
    <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
      style={{ padding: '6px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
      Subscribe Counters
    </button>
  </div>
)}
          {/* Stats */}
          <div className="clinic-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            {[
              { label: t('Waiting'), value: waitingCount, color: '#6366f1', bg: '#eef2ff' },
              { label: t('Urgent'),  value: urgentCount,  color: '#ea580c', bg: '#fff7ed' },
              { label: t('Done'),    value: doneCount,    color: '#16a34a', bg: '#f0fdf4' },
              { label: 'Today',   value: queueData?.today_counter || 0, color: '#0f172a', bg: '#f1f5f9' },
            ].map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
              </div>
            ))}
          </div>
{/* Appointments Button */}
          {appointments.length > 0 && (
            <button onClick={() => setShowAppointmentsModal(true)} style={{
              width: '100%', padding: '12px 16px', borderRadius: 12,
              background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)',
              border: '1.5px solid #bae6fd', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0369a1' }}>📅 Upcoming Appointments</div>
              <div style={{ background: '#0369a1', color: '#fff', borderRadius: 99, fontSize: 12, fontWeight: 800, padding: '2px 10px' }}>{appointments.length}</div>
            </button>
          )}


          {/* Counter panels */}
          {counters.length > 0 && (
            <div className="clinic-counters-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(counters.length, 3)}, 1fr)`, gap: 10 }}>
              {counters.map(c => {
                const calledToken = tokens.find(t => t.token_code === c.called_token_code);
                const cWaiting = tokens.filter(t => t.counter_code === c.code && ['waiting', 'urgent'].includes(t.status)).length;
                return (
                  <div key={c.code} style={{
  background: c.locked ? '#475569' : 'linear-gradient(135deg,#0f172a,#1e293b)',
  borderRadius: 14, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10,
  opacity: c.locked ? 0.6 : 1, position: 'relative',
}}>
  {c.locked && (
    <div style={{ position: 'absolute', top: 8, right: 8, background: '#92400e', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 99 }}>
      🔒 Locked
    </div>
  )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>📍 {c.name}</div>
                        {c.description && <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{c.description}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 32, fontWeight: 900, color: '#fff', letterSpacing: 2 }}>
                          {c.current_token ? String(c.current_token).padStart(3, '0') : '---'}
                        </div>
                        {calledToken && <div style={{ fontSize: 10, color: '#94a3b8' }}>{calledToken.customer_name}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                     <button onClick={() => qPerms.callNext && callNext(c.code)} disabled={cWaiting === 0 || c.locked || !qPerms.callNext || callingCounter === c.code}
  style={{
    flex: 1, padding: '8px', borderRadius: 8, border: 'none',
    background: cWaiting > 0 ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#374151',
    color: '#fff', fontWeight: 700, fontSize: 12,
    cursor: cWaiting > 0 && callingCounter !== c.code ? 'pointer' : 'not-allowed',
    opacity: callingCounter === c.code ? 0.6 : 1,
  }}>
  {callingCounter === c.code ? '📢 Calling...' : `📢 Call Next ${cWaiting > 0 ? `(${cWaiting})` : ''}`}
</button>
                      <button onClick={() => {
                        const deskUrl = `${window.location.origin}/queue/desk?counter=${c.code}`;
                        navigator.clipboard.writeText(deskUrl);
                        alert(`Desk link copied!\n${deskUrl}`);
                      }} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#94a3b8', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                        🔗
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {counters.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 10, border: '2px dashed #e2e8f0', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>No counters yet — click Counters to add</div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input type="date" value={viewDate} onChange={e => setViewDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#0f172a', outline: 'none' }} />
            {counters.length > 0 && (
              <select value={filterCounter} onChange={e => setFilterCounter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#0f172a', outline: 'none', background: '#fff' }}>
                <option value="all">All {queueData?.counter_label || 'Counter'}s</option>
                {counters.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                <option value="unassigned">Unassigned</option>
              </select>
            )}
            <div style={{ display: 'flex', gap: 3, background: '#f1f5f9', borderRadius: 8, padding: 3, flexWrap: 'wrap' }}>
              {['all', 'waiting', 'urgent', 'called', 'hold', 'done', 'skipped', 'cancelled'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: 10, fontWeight: filterStatus === s ? 700 : 500,
                  background: filterStatus === s ? '#fff' : 'transparent',
                  color: filterStatus === s ? '#0f172a' : '#64748b',
                  boxShadow: filterStatus === s ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  textTransform: 'capitalize',
                }}>
                  {s === 'all' ? `All (${tokens.length})` : `${STATUS_META[s]?.emoji || ''} ${s} (${tokens.filter(t => t.status === s).length})`}
                </button>
              ))}
            </div>
            <button onClick={fetchQueue} style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
              ↺ Refresh
            </button>
          </div>

          {/* Token list */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: '#94a3b8', flexDirection: 'column', gap: 8 }}>
              <div style={{ width: 24, height: 24, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              Loading queue...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎫</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>No tokens</div>
              <div style={{ fontSize: 12 }}>Issue a token using the panel on the right.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.filter(t => t.status === 'urgent').map(t => (
                <TokenCard key={t.id} token={t} onAction={qPerms.updateStatus ? doAction : () => {}} counterLabel={queueData?.counter_label || 'Counter'}
                  onPrint={(tok, type) => queueData && printSlip(tok, queueData, type, slipTranslations, translatedName, LANG_NAME_TO_CODE[translateLang] || '')}
                  onWA={async (tok) => {
  if (!tok.customer_phone) return;
  const digits = tok.customer_phone.replace(/\D/g, '');
  const phone = digits.length === 10 ? `91${digits}` : digits;
  const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
  const rawNum = String(tok.token_number).padStart(3, '0');
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const caption = `🎫 Token: *${rawNum}*\n👤 ${tok.customer_name}${tok.counter_name ? `\n📍 ${queueData?.counter_label || 'Counter'}: ${tok.counter_name}` : ''}\n\nTrack your turn:\n${trackUrl}\n\n🔔 Alarm fires when it's your turn!\n\n— ${activeBrand}`;
  try {
   const langCodeMap: Record<string, string> = {
  'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
  'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
  'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
};
const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
  method: 'POST', headers: authHeaders(),
  body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${rawNum}.pdf` }),
});
    let waRes;
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
      waRes = await fetch(`${BASE}/whatsapp/send-pdf`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ phone, base64, filename: `Token_${rawNum}.pdf`, caption, outlet_id: outletId }),
      });
    } else {
      waRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    }
    const waData = await waRes.json().catch(() => ({}));
    if (waData.error) alert(`⚠️ ${waData.error}`);
    else alert('✅ Sent!');
  } catch (e) {
    const fallbackRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    const fallbackData = await fallbackRes.json().catch(() => ({}));
    if (fallbackData.error) alert(`⚠️ ${fallbackData.error}`);
    else alert('✅ Sent (PDF failed, text only)');
  }
}}
onEmail={async (tok) => {
  if (!tok.customer_email) return;
  const num = String(tok.token_number).padStart(3, '0');
  const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
  const langCodeMap: Record<string, string> = {
    'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
    'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
    'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
  };
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const emailHtml = `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#6366f1">🎫 Token #${num}</h2><p>Hi <strong>${tok.customer_name}</strong>!</p>${tok.counter_name ? `<p>📍 ${queueData?.counter_label || 'Counter'}: <strong>${tok.counter_name}</strong></p>` : ''}${tok.service ? `<p>🔧 ${tok.service}</p>` : ''}<p>Your token slip is attached as a PDF.</p><a href="${trackUrl}" style="display:inline-block;margin-top:16px;padding:12px 24px;background:#6366f1;color:#fff;border-radius:10px;text-decoration:none;font-weight:700">🔔 Track Your Turn</a><p style="margin-top:20px;font-size:12px;color:#94a3b8">— ${activeBrand}</p></div>`;
  try {
    const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${num}.pdf` }),
    });
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          to: tok.customer_email,
          subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`,
          html: emailHtml,
          attachments: [{ filename: `Token_${num}.pdf`, content: base64, encoding: 'base64' }],
          outlet_id: outletId,
        }),
      });
    } else {
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ to: tok.customer_email, subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`, html: emailHtml, outlet_id: outletId }),
      });
    }
    alert('✅ Email sent!');
  } catch (e) { alert('❌ Email failed'); }
}}
onEdit={(tok) => { setEditForm({ customer_name: tok.customer_name, customer_phone: tok.customer_phone || '', customer_email: tok.customer_email || '', counter_code: tok.counter_code || '', service: tok.service || '', notes: tok.notes || '', appointment_time: tok.appointment_time || '' }); setEditingToken(tok); }}
onDelete={(tok) => { if (!confirm(`Delete token #${String(tok.token_number).padStart(3,'0')} for ${tok.customer_name}?`)) return; fetch(`${BASE}/queue/tokens/${tok.id}`, { method: 'DELETE', headers: authHeaders() }).then(() => fetchQueue()); }}
                canSendWA={qPerms.sendWhatsApp}
                canSendEmail={qPerms.sendEmail}
                canEditToken={qPerms.editToken}
                canDeleteToken={qPerms.deleteToken}
                />
              ))}
             {filtered.filter(t => t.status === 'called').map(t => (
  <TokenCard key={t.id} token={t} onAction={qPerms.updateStatus ? doAction : () => {}} counterLabel={queueData?.counter_label || 'Counter'}
                  onPrint={(tok, type) => queueData && printSlip(tok, queueData, type, slipTranslations, translatedName, LANG_NAME_TO_CODE[translateLang] || '')}
                  onWA={async (tok) => {
  if (!tok.customer_phone) return;
  const digits = tok.customer_phone.replace(/\D/g, '');
  const phone = digits.length === 10 ? `91${digits}` : digits;
  const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
  const rawNum = String(tok.token_number).padStart(3, '0');
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const caption = `🎫 Token: *${rawNum}*\n👤 ${tok.customer_name}${tok.counter_name ? `\n📍 ${queueData?.counter_label || 'Counter'}: ${tok.counter_name}` : ''}\n\nTrack your turn:\n${trackUrl}\n\n🔔 Alarm fires when it's your turn!\n\n— ${activeBrand}`;
  try {
   const langCodeMap: Record<string, string> = {
  'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
  'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
  'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
};
const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
  method: 'POST', headers: authHeaders(),
  body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${rawNum}.pdf` }),
});
    let waRes;
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
      waRes = await fetch(`${BASE}/whatsapp/send-pdf`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ phone, base64, filename: `Token_${rawNum}.pdf`, caption, outlet_id: outletId }),
      });
    } else {
      waRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    }
    const waData = await waRes.json().catch(() => ({}));
    if (waData.error) alert(`⚠️ ${waData.error}`);
    else alert('✅ Sent!');
  } catch (e) {
    const fallbackRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    const fallbackData = await fallbackRes.json().catch(() => ({}));
    if (fallbackData.error) alert(`⚠️ ${fallbackData.error}`);
    else alert('✅ Sent (PDF failed, text only)');
  }
}}
onEmail={async (tok) => {
  if (!tok.customer_email) return;
  const num = String(tok.token_number).padStart(3, '0');
  const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
  const langCodeMap: Record<string, string> = {
    'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
    'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
    'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
  };
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const emailHtml = `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#6366f1">🎫 Token #${num}</h2><p>Hi <strong>${tok.customer_name}</strong>!</p>${tok.counter_name ? `<p>📍 ${queueData?.counter_label || 'Counter'}: <strong>${tok.counter_name}</strong></p>` : ''}${tok.service ? `<p>🔧 ${tok.service}</p>` : ''}<p>Your token slip is attached as a PDF.</p><a href="${trackUrl}" style="display:inline-block;margin-top:16px;padding:12px 24px;background:#6366f1;color:#fff;border-radius:10px;text-decoration:none;font-weight:700">🔔 Track Your Turn</a><p style="margin-top:20px;font-size:12px;color:#94a3b8">— ${activeBrand}</p></div>`;
  try {
    const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${num}.pdf` }),
    });
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          to: tok.customer_email,
          subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`,
          html: emailHtml,
          attachments: [{ filename: `Token_${num}.pdf`, content: base64, encoding: 'base64' }],
          outlet_id: outletId,
        }),
      });
    } else {
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ to: tok.customer_email, subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`, html: emailHtml, outlet_id: outletId }),
      });
    }
    alert('✅ Email sent!');
  } catch (e) { alert('❌ Email failed'); }
}}
onEdit={(tok) => { setEditForm({ customer_name: tok.customer_name, customer_phone: tok.customer_phone || '', customer_email: tok.customer_email || '', counter_code: tok.counter_code || '', service: tok.service || '', notes: tok.notes || '', appointment_time: tok.appointment_time || '' }); setEditingToken(tok); }}
onDelete={(tok) => { if (!confirm(`Delete token #${String(tok.token_number).padStart(3,'0')} for ${tok.customer_name}?`)) return; fetch(`${BASE}/queue/tokens/${tok.id}`, { method: 'DELETE', headers: authHeaders() }).then(() => fetchQueue()); }}
                canSendWA={qPerms.sendWhatsApp}
                canSendEmail={qPerms.sendEmail}
                canEditToken={qPerms.editToken}
                canDeleteToken={qPerms.deleteToken}
                />
              ))}
             {filtered.filter(t => !['urgent', 'called'].includes(t.status)).map(t => (
  <TokenCard key={t.id} token={t} onAction={qPerms.updateStatus ? doAction : () => {}} counterLabel={queueData?.counter_label || 'Counter'}
                  onPrint={(tok, type) => queueData && printSlip(tok, queueData, type, slipTranslations, translatedName, LANG_NAME_TO_CODE[translateLang] || '')}
                  onWA={async (tok) => {
                    if (!tok.customer_phone) return;
                    const digits = tok.customer_phone.replace(/\D/g, '');
                    const phone = digits.length === 10 ? `91${digits}` : digits;
                    const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
                    const rawNum = String(tok.token_number).padStart(3, '0');
                    const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const caption = `🎫 Token: *${rawNum}*\n👤 ${tok.customer_name}${tok.counter_name ? `\n📍 ${queueData?.counter_label || 'Counter'}: ${tok.counter_name}` : ''}\n\nTrack your turn:\n${trackUrl}\n\n🔔 Alarm fires when it's your turn!\n\n— ${activeBrand}`;
                    try {
                      const langCodeMap: Record<string, string> = {
  'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
  'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
  'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
};
const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
                      const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
  method: 'POST', headers: authHeaders(),
  body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${rawNum}.pdf` }),
});
                      let waRes;
                      if (pdfRes.ok) {
                        const { base64 } = await pdfRes.json();
                        waRes = await fetch(`${BASE}/whatsapp/send-pdf`, {
                          method: 'POST', headers: authHeaders(),
                          body: JSON.stringify({ phone, base64, filename: `Token_${rawNum}.pdf`, caption, outlet_id: outletId }),
                        });
                      } else {
                        waRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
                      }
                      const waData = await waRes.json().catch(() => ({}));
                      if (waData.error) alert(`⚠️ ${waData.error}`);
                      else alert('✅ Sent!');
                    } catch (e) {
                      const fallbackRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
                      const fallbackData = await fallbackRes.json().catch(() => ({}));
                      if (fallbackData.error) alert(`⚠️ ${fallbackData.error}`);
                      else alert('✅ Sent (PDF failed, text only)');
                    }
                  }}
                  onEmail={async (tok) => {
  if (!tok.customer_email) return;
  const num = String(tok.token_number).padStart(3, '0');
  const trackUrl = `${window.location.origin}/queue/track?token=${tok.token_code}`;
  const langCodeMap: Record<string, string> = {
    'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
    'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
    'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
  };
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const emailHtml = `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px"><h2 style="color:#6366f1">🎫 Token #${num}</h2><p>Hi <strong>${tok.customer_name}</strong>!</p>${tok.counter_name ? `<p>📍 ${queueData?.counter_label || 'Counter'}: <strong>${tok.counter_name}</strong></p>` : ''}${tok.service ? `<p>🔧 ${tok.service}</p>` : ''}<p>Your token slip is attached as a PDF.</p><a href="${trackUrl}" style="display:inline-block;margin-top:16px;padding:12px 24px;background:#6366f1;color:#fff;border-radius:10px;text-decoration:none;font-weight:700">🔔 Track Your Turn</a><p style="margin-top:20px;font-size:12px;color:#94a3b8">— ${activeBrand}</p></div>`;
  try {
    const html = await buildSlipHtml(tok, queueData!, slipTranslations, translatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${num}.pdf` }),
    });
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          to: tok.customer_email,
          subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`,
          html: emailHtml,
          attachments: [{ filename: `Token_${num}.pdf`, content: base64, encoding: 'base64' }],
          outlet_id: outletId,
        }),
      });
    } else {
      await fetch(`${BASE}/email/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ to: tok.customer_email, subject: `Your Queue Token #${num} — ${queueData?.queue_name || ''}`, html: emailHtml, outlet_id: outletId }),
      });
    }
    alert('✅ Email sent!');
  } catch (e) { alert('❌ Email failed'); }
}}
onEdit={(tok) => { setEditForm({ customer_name: tok.customer_name, customer_phone: tok.customer_phone || '', customer_email: tok.customer_email || '', counter_code: tok.counter_code || '', service: tok.service || '', notes: tok.notes || '', appointment_time: tok.appointment_time || '' }); setEditingToken(tok); }}
onDelete={(tok) => { if (!confirm(`Delete token #${String(tok.token_number).padStart(3,'0')} for ${tok.customer_name}?`)) return; fetch(`${BASE}/queue/tokens/${tok.id}`, { method: 'DELETE', headers: authHeaders() }).then(() => fetchQueue()); }}
                canSendWA={qPerms.sendWhatsApp}
                canSendEmail={qPerms.sendEmail}
                canEditToken={qPerms.editToken}
                canDeleteToken={qPerms.deleteToken}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Issue Token Panel ── */}
     <div
  id="issue-panel"
  className="clinic-issue-panel"
  style={{
    minWidth: 380, maxWidth: 700, flexShrink: 0,
    display: 'flex', flexDirection: 'column',
    background: '#fff', borderLeft: '1px solid #e2e8f0', overflow: 'hidden',
    position: 'relative',
  }}
>
  {/* Resize handle */}
  <div
    className="clinic-resize-handle"
    onMouseDown={e => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = panelWidth;
      const onMove = (ev: MouseEvent) => setPanelWidth(Math.max(380, Math.min(700, startW - (ev.clientX - startX))));
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }}
    style={{
      position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
      cursor: 'ew-resize', zIndex: 10,
      background: 'transparent',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = '#6366f1')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
  />
        {/* Panel header */}
        <div style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)', padding: '16px 20px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            {/* Left title */}
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>
              🎫 Issue Token
            </div>

            {/* Right toolbar buttons */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>

              {/* Reset / Clear Form */}
              <button
                onClick={() => {
                  setForm(p => ({ ...p, customer_name: '', customer_phone: '', customer_email: '', notes: '', appointment_time: '', is_walk_in: true }));
                  setPhoneResetKey(k => k + 1);
                }}
                title="Reset / Clear form"
                style={{ ...clinicToolbarBtn(), width: 36, height: 36, padding: 0 }}
              >
                <RotateCcw size={16} />
              </button>

              {/* Bilingual toggle */}
              <button
                onClick={() => {
                  const next = !bilingualEnabled;
                  setBilingualEnabled(next);
                  localStorage.setItem('cloudairambo_queue_bilingual_enabled', String(next));
                }}
                title={bilingualEnabled ? 'Bilingual slip: ON' : 'Bilingual slip: OFF'}
                style={{
                  ...clinicToolbarBtn(bilingualEnabled),
                  minWidth: 72,
                  padding: '0 10px',
                  justifyContent: 'center',
                }}
              >
                🌐 {bilingualEnabled ? 'ON' : 'OFF'}
              </button>

              {/* Language selection dropdown */}
              <select
                disabled={!bilingualEnabled}
                value={translateLang}
                onChange={e => { setTranslateLang(e.target.value); localStorage.setItem('queue_translate_lang', e.target.value); }}
                style={{
                  fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.14)', color: '#fff', height: 36,
                  padding: '0 8px', borderRadius: 10, cursor: 'pointer', outline: 'none', maxWidth: 130,
                  opacity: bilingualEnabled ? 1 : 0.45,
                }}
              >
                <option value="" style={{ color: '#111' }}>English only</option>
                {[
                 ['af','AF — Afrikaans'],['sq','SQ — Albanian'],['am','AM — Amharic'],['ar','AR — Arabic'],['hy','HY — Armenian'],
            ['az','AZ — Azerbaijani'],['eu','EU — Basque'],['be','BE — Belarusian'],['bn','BN — Bengali'],['bs','BS — Bosnian'],
            ['bg','BG — Bulgarian'],['ca','CA — Catalan'],['ceb','CEB — Cebuano'],['ny','NY — Chichewa'],['zh','ZH — Chinese (Simplified)'],
            ['zh-TW','ZH-TW — Chinese (Traditional)'],['co','CO — Corsican'],['hr','HR — Croatian'],['cs','CS — Czech'],['da','DA — Danish'],
            ['nl','NL — Dutch'],['en','EN — English'],['eo','EO — Esperanto'],['et','ET — Estonian'],['tl','TL — Filipino'],
            ['fi','FI — Finnish'],['fr','FR — French'],['fy','FY — Frisian'],['gl','GL — Galician'],['ka','KA — Georgian'],
            ['de','DE — German'],['el','EL — Greek'],['gu','GU — Gujarati'],['ht','HT — Haitian Creole'],['ha','HA — Hausa'],
            ['haw','HAW — Hawaiian'],['iw','IW — Hebrew'],['hi','HI — Hindi'],['hmn','HMN — Hmong'],['hu','HU — Hungarian'],
            ['is','IS — Icelandic'],['ig','IG — Igbo'],['id','ID — Indonesian'],['ga','GA — Irish'],['it','IT — Italian'],
            ['ja','JA — Japanese'],['jw','JW — Javanese'],['kn','KN — Kannada'],['kk','KK — Kazakh'],['km','KM — Khmer'],
            ['rw','RW — Kinyarwanda'],['ko','KO — Korean'],['ku','KU — Kurdish (Kurmanji)'],['ky','KY — Kyrgyz'],['lo','LO — Lao'],
            ['la','LA — Latin'],['lv','LV — Latvian'],['lt','LT — Lithuanian'],['lb','LB — Luxembourgish'],['mk','MK — Macedonian'],
            ['mg','MG — Malagasy'],['ms','MS — Malay'],['ml','ML — Malayalam'],['mt','MT — Maltese'],['mi','MI — Maori'],
            ['mr','MR — Marathi'],['mn','MN — Mongolian'],['my','MY — Myanmar (Burmese)'],['ne','NE — Nepali'],['no','NO — Norwegian'],
            ['or','OR — Odia (Oriya)'],['ps','PS — Pashto'],['fa','FA — Persian'],['pl','PL — Polish'],['pt','PT — Portuguese'],
            ['pa','PA — Punjabi'],['ro','RO — Romanian'],['ru','RU — Russian'],['sm','SM — Samoan'],['gd','GD — Scots Gaelic'],
            ['sr','SR — Serbian'],['st','ST — Sesotho'],['sn','SN — Shona'],['sd','SD — Sindhi'],['si','SI — Sinhala'],
            ['sk','SK — Slovak'],['sl','SL — Slovenian'],['so','SO — Somali'],['es','ES — Spanish'],['su','SU — Sundanese'],
            ['sw','SW — Swahili'],['sv','SV — Swedish'],['tg','TG — Tajik'],['ta','TA — Tamil'],['tt','TT — Tatar'],
            ['te','TE — Telugu'],['th','TH — Thai'],['tr','TR — Turkish'],['tk','TK — Turkmen'],['ug','UG — Uyghur'],
            ['uk','UK — Ukrainian'],['ur','UR — Urdu'],['uz','UZ — Uzbek'],['vi','VI — Vietnamese'],['cy','CY — Welsh'],
            ['xh','XH — Xhosa'],['yi','YI — Yiddish'],['yo','YO — Yoruba'],['zu','ZU — Zulu'],['ak','AK — Akan'],
            ['bho','BHO — Bhojpuri'],['doi','DOI — Dogri'],['dv','DV — Dhivehi'],['ee','EE — Ewe'],['gn','GN — Guarani'],
            ['ilo','ILO — Ilocano'],['kri','KRI — Krio'],['ckb','CKB — Kurdish (Sorani)'],['lg','LG — Luganda'],['mai','MAI — Maithili'],
            ['lus','LUS — Mizo'],['om','OM — Oromo'],['qu','QU — Quechua'],['sa','SA — Sanskrit'],['nso','NSO — Sepedi'],
            ['ti','TI — Tigrinya'],['ts','TS — Tsonga'],
                ].map(([val, label]) => (
                  <option key={val} value={val} style={{ color: '#111' }}>{label}</option>
                ))}
              </select>

              {/* Translate indicator or live badge */}
              {translateLang && bilingualEnabled && (
                <span
                  title={slipTranslating ? 'Translating...' : 'Translation active'}
                  style={{
                    ...clinicToolbarBtn(true),
                    width: 36,
                    height: 36,
                    padding: 0,
                    background: slipTranslating ? 'rgba(251,191,36,0.35)' : 'rgba(34,197,94,0.35)',
                    borderColor: slipTranslating ? 'rgba(251,191,36,0.6)' : 'rgba(34,197,94,0.6)',
                  }}
                >
                  ⚡
                </span>
              )}

              {/* Undo translation */}
              {Object.keys(slipTranslations).length > 0 && (
                <button
                  onClick={() => { setSlipTranslations({}); setTranslateLang(''); localStorage.removeItem('queue_translate_lang'); }}
                  title="Undo translation"
                  style={{
                    ...clinicToolbarBtn(),
                    width: 36,
                    height: 36,
                    padding: 0,
                    background: 'rgba(248,113,113,0.28)',
                    borderColor: 'rgba(248,113,113,0.4)',
                  }}
                >
                  ↩
                </button>
              )}

              {/* Divider */}
              <div style={clinicToolbarDivider} />

              {/* Preview Toggle */}
              <button
                onClick={() => setShowSlipPreview(s => !s)}
                title={showSlipPreview ? 'Hide Slip Preview' : 'Show Slip Preview'}
                style={{ ...clinicToolbarBtn(showSlipPreview), width: 36, height: 36, padding: 0 }}
              >
                👁
              </button>

            </div>
          </div>

          {lastIssued && (
            <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 800 }}>LAST ISSUED</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', letterSpacing: 2 }}>
                  {String(lastIssued.token_number).padStart(3, '0')} — {lastIssued.customer_name}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Walk-in / Appointment toggle */}
          <div style={{ display: 'flex', gap: 8 }}>
            {[true, false].map(w => (
              <button key={String(w)} onClick={() => setForm(p => ({ ...p, is_walk_in: w }))}
                style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: `2px solid ${form.is_walk_in === w ? '#6366f1' : '#cbd5e1'}`, background: form.is_walk_in === w ? '#eef2ff' : '#fff', color: form.is_walk_in === w ? '#4f46e5' : '#475569', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                {w ? `🚶 ${t('Walk-in')}` : `📅 ${t('Appointment')}`}
              </button>
            ))}
          </div>

          {/* Form fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={L}>{t(queueData?.customer_label || 'Customer')} Name *</label>
              <input ref={nameRef} style={F} placeholder="Full name" value={form.customer_name}
                onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && issueToken()} autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
  <label style={L}>Phone</label>
  <PhoneInput key={phoneResetKey} value={form.customer_phone} onChange={v => setForm(p => ({ ...p, customer_phone: v }))} inputStyle={F} />
</div>
              <div>
                <label style={L}>Email</label>
                <input style={F} placeholder="email@..." value={form.customer_email}
                  onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))} />
              </div>
            </div>
            {counters.length > 0 && (
              <div>
                <label style={L}>{t(queueData?.counter_label || 'Counter')} *</label>
                <select style={{ ...F, borderColor: !form.counter_code ? '#fecaca' : '#e2e8f0' }} value={form.counter_code}
                  onChange={e => setForm(p => ({ ...p, counter_code: e.target.value }))}>
                  <option value="" disabled>— Select {(queueData?.counter_label || 'Counter').toLowerCase()} —</option>
                  {counters.map(c => (
                    <option key={c.code} value={c.code}>{c.name}{c.description ? ` — ${c.description}` : ''}</option>
                  ))}
                </select>
                {!form.counter_code && (
                  <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginTop: 3 }}>
                    Please select a {(queueData?.counter_label || 'counter').toLowerCase()} before issuing
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={L}>{t(queueData?.service_label || 'Service')}</label>
                <input style={F} placeholder="e.g. General..." value={form.service}
                  onChange={e => setForm(p => ({ ...p, service: e.target.value }))} />
              </div>
              <div>
                <label style={L}>{t('Time Slot')}</label>
                <div style={{ position: 'relative' }}>
  <input
    readOnly
    style={{ ...F, cursor: 'pointer', paddingRight: 32 }}
    placeholder="Select time"
    value={form.appointment_time ? (() => {
      const [h, m] = form.appointment_time.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour % 12 || 12;
      return `${String(h12).padStart(2,'0')}:${m} ${ampm}`;
    })() : ''}
    onClick={openTimePicker}
  />
  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, pointerEvents: 'none' }}>🕐</span>
</div>
              </div>
            </div>
            {!form.is_walk_in && (
              <div>
                <label style={L}>Appointment Date</label>
                <input type="date" style={F}
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.appointment_date}
                  onChange={e => setForm(p => ({ ...p, appointment_date: e.target.value }))} />
                {form.appointment_date && form.appointment_date !== new Date().toISOString().slice(0, 10) && (
                  <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 600, marginTop: 3 }}>
                    📅 Booking for future date — token will appear on {new Date(form.appointment_date).toLocaleDateString('en-IN', { dateStyle: 'long' })}
                  </div>
                )}
              </div>
            )}
            <div>
              <label style={L}>{t('Notes')}</label>
              <textarea style={{ ...F, height: 50, resize: 'vertical' }} placeholder="Internal notes..."
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>

          {/* Live slip preview */}
          {showSlipPreview && (
            <div>
              {/* Template selector */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'center', marginRight: 4 }}>Slip:</div>
                {(['thermal', 'card', 'classic'] as const).map(t => (
                  <button key={t} onClick={() => setSlipTemplate(t)} style={{
                    flex: 1, padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${slipTemplate === t ? '#6366f1' : '#cbd5e1'}`,
                    background: slipTemplate === t ? '#eef2ff' : '#fff', color: slipTemplate === t ? '#4f46e5' : '#475569',
                    fontWeight: 800, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize',
                  }}>
                    {t === 'thermal' ? '🖨 Thermal' : t === 'card' ? '💳 Card' : '📜 Classic'}
                  </button>
                ))}
              </div>
              {/* Slip translation is now managed in the top header toolbar */}
              <LiveSlipPreview form={form} queueData={queueData} slipTemplate={slipTemplate} slipTranslations={slipTranslations} translatedName={translatedName} />
            </div>
          )}
        </div>

        {/* Bottom actions */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* If last issued — show print + whatsapp */}
          {lastIssued && (
            <div style={{ display: 'flex', gap: 6 }}>
              {(['thermal', 'classic', 'print'] as const).map(t => (
  <button key={t} onClick={() => queueData && printSlip(lastIssued, queueData, t, lastIssuedSlipTranslations, lastIssuedTranslatedName, LANG_NAME_TO_CODE[translateLang] || '').catch(console.error)}
    style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: '1px solid #e2e8f0', background: t === 'print' ? '#0f172a' : '#f8fafc', color: t === 'print' ? '#fff' : '#475569', fontWeight: 700, fontSize: 10, cursor: 'pointer' }}>
    {t === 'thermal' ? '🖨' : t === 'classic' ? '📜' : '🖨 Print'}
  </button>
))}
              {/* ADD THIS RIGHT HERE ↓ */}
   
              {lastIssued.customer_phone && qPerms.sendWhatsApp && (
                <button onClick={async () => {
  const digits = lastIssued.customer_phone!.replace(/\D/g, '');
  const phone = digits.length === 10 ? `91${digits}` : digits;
  const trackUrl = `${window.location.origin}/queue/track?token=${lastIssued.token_code}`;
  const rawNum = String(lastIssued.token_number).padStart(3, '0');
  const cl = queueData?.counter_label || 'Counter';
  const activeBrand = companyName || queueData?.queue_name || authUser?.name || 'Our Company';
  const caption = `🎫 Token: *${rawNum}*\n👤 ${lastIssued.customer_name}${lastIssued.counter_name ? `\n📍 ${cl}: ${lastIssued.counter_name}` : ''}\n\nTrack your turn:\n${trackUrl}\n\n🔔 Alarm fires when it's your turn!\n\n— ${activeBrand}`;
  try {
   const langCodeMap: Record<string, string> = {
  'Arabic': 'ar', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te',
  'Kannada': 'kn', 'Malayalam': 'ml', 'Urdu': 'ur', 'French': 'fr',
  'German': 'de', 'Spanish': 'es', 'Russian': 'ru', 'Japanese': 'ja',
};
const html = await buildSlipHtml(lastIssued, queueData!, lastIssuedSlipTranslations, lastIssuedTranslatedName, langCodeMap[translateLang] || '');
    const pdfRes = await fetch(`${BASE}/invoices/html-to-pdf`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ html, pageWidth: 302, filename: `Token_${rawNum}.pdf` }),
    });
    let waRes;
    if (pdfRes.ok) {
      const { base64 } = await pdfRes.json();
     waRes = await fetch(`${BASE}/whatsapp/send-pdf`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ phone, base64, filename: `Token_${rawNum}.pdf`, caption, outlet_id: outletId }),
      });
    } else {
      waRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    }
    const waData = await waRes.json().catch(() => ({}));
    postLog('QUEUE_WHATSAPP_SENT', `WhatsApp token sent to ${lastIssued.customer_name} | ${lastIssued.customer_phone} | Token #${rawNum}`);
    if (waData.error) alert(`⚠️ ${waData.error}`);
    else alert('✅ Sent!');
  } catch (e) {
    const fallbackRes = await fetch(`${BASE}/whatsapp/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone, message: caption, outlet_id: outletId }) });
    const fallbackData = await fallbackRes.json().catch(() => ({}));
    if (fallbackData.error) alert(`⚠️ ${fallbackData.error}`);
    else alert('✅ Sent (PDF failed, text only)');
  }
}} style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 10, cursor: 'pointer' }}>
                  💬 WA
                </button>
              )}
            </div>
          )}

          <button onClick={issueToken} disabled={saving || !form.customer_name.trim() || (counters.length > 0 && !form.counter_code)}
            style={{
              width: '100%', padding: '15px', borderRadius: 12, border: 'none',
              background: (!form.customer_name.trim() || (counters.length > 0 && !form.counter_code)) ? '#e2e8f0' : 'linear-gradient(135deg,#6366f1,#4f46e5)',
              color: (!form.customer_name.trim() || (counters.length > 0 && !form.counter_code)) ? '#94a3b8' : '#fff',
              fontWeight: 800, fontSize: 16, cursor: (!form.customer_name.trim() || (counters.length > 0 && !form.counter_code)) ? 'not-allowed' : 'pointer',
              boxShadow: (form.customer_name.trim() && (counters.length === 0 || form.counter_code)) ? '0 6px 20px rgba(99,102,241,0.4)' : 'none',
            }}>
            {saving ? 'Issuing...' : `✅ ${t('Issue Token')}`}
          </button>
        </div>
      </div>
{/* ── Appointments Modal ── */}
      {showAppointmentsModal && (
        <ModalWrap title="📅 Upcoming Appointments" onClose={() => setShowAppointmentsModal(false)} width={600}>
          <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 480 }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '90px 60px 1fr 100px 90px 80px', gap: 0, padding: '8px 16px', background: '#f1f5f9', borderRadius: '8px 8px 0 0', borderBottom: '1px solid #e2e8f0' }}>
              {['Date', 'Token', queueData?.customer_label || 'Customer', 'Phone', 'Time', queueData?.service_label || 'Service'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {appointments.map((appt, i) => (
              <div key={appt.id} style={{
                display: 'grid', gridTemplateColumns: '90px 60px 1fr 100px 90px 80px',
                gap: 0, padding: '12px 16px', alignItems: 'center',
                background: i % 2 === 0 ? '#fff' : '#fafafa',
                borderBottom: '1px solid #f1f5f9',
                borderRadius: i === appointments.length - 1 ? '0 0 8px 8px' : 0,
              }}>
                {/* Date */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
                    {new Date(appt.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>
                    {new Date(appt.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' })}
                  </div>
                </div>
                {/* Token */}
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#fff' }}>
                  {String(appt.token_number).padStart(3, '0')}
                </div>
                {/* Name + email */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{appt.customer_name}</div>
                  {appt.customer_email && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{appt.customer_email}</div>}
                  {appt.counter_name && <div style={{ fontSize: 10, color: '#6366f1', marginTop: 1 }}>📍 {appt.counter_name}</div>}
                </div>
                {/* Phone */}
                <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{appt.customer_phone || '—'}</div>
                {/* Time */}
                <div style={{ fontSize: 12, fontWeight: 700, color: appt.appointment_time ? '#0f172a' : '#94a3b8' }}>
                  {appt.appointment_time || '—'}
                </div>
                {/* Service */}
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{appt.service || '—'}</div>
              </div>
            ))}
          </div>
          </div>
        </ModalWrap>
      )}
      {/* ── Counters Modal ── */}
      {showCounters && (
        <ModalWrap title={`📍 Manage ${queueData?.counter_label || 'Counter'}s`} onClose={() => { setShowCounters(false); fetchQueue(); }} width={500}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={L}>Add New {queueData?.counter_label || 'Counter'}</label>
              <input style={F} placeholder="e.g. Counter 1, Window A, Station 3" value={newCounterName}
                onChange={e => setNewCounterName(e.target.value)} />
              <input style={F} placeholder="Description (optional)" value={newCounterDesc}
                onChange={e => setNewCounterDesc(e.target.value)} />
              {outlets.length > 0 && (
                <select style={F} value={newCounterOutlet} onChange={e => setNewCounterOutlet(e.target.value)}>
                  <option value="">
                    {outletId ? `Use active outlet (${outlets.find(o => o.id === outletId)?.name || 'current'})` : 'No outlet (unassigned)'}
                  </option>
                  {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
              <button onClick={createCounter} disabled={!newCounterName.trim()}
                style={{ padding: '9px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: newCounterName.trim() ? 1 : 0.5 }}>
                ➕ Add {queueData?.counter_label || 'Counter'}
              </button>
            </div>
            {counters.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={L}>Existing {queueData?.counter_label || 'Counter'}s</label>
                {counters.map(c => {
                  const deskUrl = `${window.location.origin}/queue/desk?counter=${c.code}`;
                  const counterOutlet = outlets.find(o => o.id === c.outlet_id);
                  return (
                    <div key={c.code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: c.locked ? '#fef3c7' : '#f8fafc', borderRadius: 10, border: `1px solid ${c.locked ? '#fde68a' : '#e2e8f0'}`, gap: 8 }}>
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
      {c.name}{c.locked && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#92400e', background: '#fde68a', padding: '1px 6px', borderRadius: 99 }}>🔒 Locked</span>}
    </div>
                        {counterOutlet && <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 600, marginTop: 1 }}>📍 {counterOutlet.name}</div>}
                        {c.description && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.description}</div>}
                        <div style={{ fontSize: 9, color: '#6366f1', marginTop: 3, wordBreak: 'break-all' }}>{deskUrl}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        <button onClick={() => { navigator.clipboard.writeText(deskUrl); alert('Desk link copied!'); }}
                          style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #e0e7ff', background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                          🔗 Link
                        </button>
                       
                        <button onClick={() => deleteCounter(c.code)}
                          style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ModalWrap>
      )}

      {/* ── Logs Modal ── */}
      {showLogs && (
        <ModalWrap title="📋 Queue Logs (Permanent)" onClose={() => setShowLogs(false)} width={560}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No logs yet</div>
            ) : logs.map((log, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 9, fontWeight: 700, background: '#0f172a', color: '#fff', padding: '2px 7px', borderRadius: 99, whiteSpace: 'nowrap' }}>{log.action}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{log.detail}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{new Date(log.time).toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))}
          </div>
        </ModalWrap>
      )}

      {/* ── Settings Modal ── */}
      {showSettings && (
  <ModalWrap title="⚙ Queue Settings" onClose={() => setShowSettings(false)} width={640}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Queue Name', key: 'queue_name', placeholder: 'e.g. City Clinic, HDFC Bank' },
              { label: 'Customer Label', key: 'customer_label', placeholder: 'Patient / Customer / Visitor' },
              { label: 'Counter Label', key: 'counter_label', placeholder: 'Doctor / Counter / Teller' },
              { label: 'Service Label', key: 'service_label', placeholder: 'Department / Service / Category' },
            ].map(({ label, key, placeholder }) => (
              <div key={key}>
                <label style={L}>{label}</label>
                <input style={F} placeholder={placeholder} value={(settingsForm as any)[key]}
                  onChange={e => setSettingsForm(p => ({ ...p, [key]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label style={L}>Avg. Time per Token (minutes)</label>
              <input type="number" style={F} value={settingsForm.avg_wait_mins}
                onChange={e => setSettingsForm(p => ({ ...p, avg_wait_mins: Number(e.target.value) }))} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Daily Reset</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Token counter resets to 001 every day</div>
              </div>
              <button onClick={() => setSettingsForm(p => ({ ...p, daily_reset: !p.daily_reset }))} style={{
                width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: settingsForm.daily_reset ? '#16a34a' : '#d1d5db', position: 'relative',
              }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: settingsForm.daily_reset ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
              </button>
            </div>

<div>
  <label style={L}>🔊 Announcement Languages</label>
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {(['1st', '2nd', '3rd'] as const).map((label, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', minWidth: 50 }}>{label}</span>
        <select
          value={ttsLangs[i]}
          onChange={e => {
            const updated = [...ttsLangs];
            updated[i] = e.target.value;
            setTtsLangs(updated);
            const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
            fetch(`${BASE}/queue/settings${settingsQ}`, {
              method: 'PATCH', headers: authHeaders(),
              body: JSON.stringify({ [`tts_lang_${i + 1}`]: e.target.value }),
            }).catch(() => {});
          }}
          style={{ ...F, flex: 1 }}
        >
      <option value="">— None —</option>
<option value="en">English</option>
{/* Indian */}
<option value="hi">Hindi</option>
<option value="pa">Punjabi</option>
<option value="ur">Urdu</option>
<option value="ta">Tamil</option>
<option value="te">Telugu</option>
<option value="kn">Kannada</option>
<option value="ml">Malayalam</option>
<option value="mr">Marathi</option>
<option value="gu">Gujarati</option>
<option value="bn">Bengali</option>
<option value="si">Sinhala</option>
<option value="ne">Nepali</option>
{/* Middle East */}
<option value="ar">Arabic</option>
<option value="he">Hebrew</option>
<option value="fa">Persian</option>
<option value="ps">Pashto</option>
{/* European */}
<option value="fr">French</option>
<option value="es">Spanish</option>
<option value="de">German</option>
<option value="it">Italian</option>
<option value="nl">Dutch</option>
<option value="pl">Polish</option>
<option value="ru">Russian</option>
<option value="uk">Ukrainian</option>
<option value="ro">Romanian</option>
<option value="cs">Czech</option>
<option value="hu">Hungarian</option>
<option value="fi">Finnish</option>
<option value="sv">Swedish</option>
<option value="no">Norwegian</option>
<option value="da">Danish</option>
<option value="el">Greek</option>
<option value="pt">Portuguese</option>
<option value="tr">Turkish</option>
<option value="bg">Bulgarian</option>
<option value="hr">Croatian</option>
<option value="sk">Slovak</option>
<option value="sl">Slovenian</option>
<option value="sr">Serbian</option>
<option value="lt">Lithuanian</option>
<option value="lv">Latvian</option>
<option value="et">Estonian</option>
<option value="is">Icelandic</option>
<option value="mk">Macedonian</option>
<option value="sq">Albanian</option>
<option value="bs">Bosnian</option>
<option value="ca">Catalan</option>
<option value="gl">Galician</option>
<option value="cy">Welsh</option>
<option value="ga">Irish</option>
<option value="mt">Maltese</option>
<option value="af">Afrikaans</option>
{/* Asian */}
<option value="zh-CN">Chinese</option>
<option value="ja">Japanese</option>
<option value="ko">Korean</option>
<option value="vi">Vietnamese</option>
<option value="th">Thai</option>
<option value="id">Indonesian</option>
<option value="ms">Malay</option>
<option value="km">Khmer</option>
<option value="lo">Lao</option>
<option value="my">Burmese</option>
<option value="mn">Mongolian</option>
<option value="jv">Javanese</option>
<option value="su">Sundanese</option>
<option value="fil">Filipino</option>
<option value="ka">Georgian</option>
<option value="kk">Kazakh</option>
<option value="az">Azerbaijani</option>
<option value="uz">Uzbek</option>
{/* African */}
<option value="sw">Swahili</option>
<option value="so">Somali</option>
<option value="am">Amharic</option>
<option value="zu">Zulu</option>
<option value="ha">Hausa</option>
<option value="yo">Yoruba</option>
</select>
      </div>
    ))}
  </div>
  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
    Announcement plays in each language in order when token is called.
  </div>
  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
  <button
    onClick={() => {
      setTtsGender('female');
      const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
      fetch(`${BASE}/queue/settings${settingsQ}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ tts_gender: 'female' }) }).catch(() => {});
    }}
    style={{
      flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid',
      borderColor: ttsGender === 'female' ? '#ec4899' : '#e2e8f0',
      background: ttsGender === 'female' ? '#fdf2f8' : '#fff',
      color: ttsGender === 'female' ? '#ec4899' : '#94a3b8',
      fontWeight: 700, fontSize: 12, cursor: 'pointer'
    }}>
    👩 Female Voice
  </button>
  <button
    onClick={() => {
      setTtsGender('male');
      const settingsQ = outletId ? `?outlet_id=${outletId}` : '';
      fetch(`${BASE}/queue/settings${settingsQ}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ tts_gender: 'male' }) }).catch(() => {});
    }}
    style={{
      flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid',
      borderColor: ttsGender === 'male' ? '#6366f1' : '#e2e8f0',
      background: ttsGender === 'male' ? '#eef2ff' : '#fff',
      color: ttsGender === 'male' ? '#6366f1' : '#94a3b8',
      fontWeight: 700, fontSize: 12, cursor: 'pointer'
    }}>
    👨 Male Voice
  </button>
</div>
</div>

<div>
  <label style={L}>🖥️ Display Screen Layout</label>
  <div style={{ display: 'flex', gap: 6 }}>
    {(['all', '1', '2', '3'] as const).map(opt => (
      <button key={opt}
        onClick={() => { setDisplayLayout(opt); localStorage.setItem('display_layout', opt); }}
        style={{
          flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid',
          borderColor: displayLayout === opt ? '#6366f1' : '#e2e8f0',
          background: displayLayout === opt ? '#eef2ff' : '#fff',
          color: displayLayout === opt ? '#6366f1' : '#94a3b8',
          fontWeight: 700, fontSize: 12, cursor: 'pointer',
        }}>
        {opt === 'all' ? 'All in One' : `${opt} per screen`}
      </button>
    ))}
  </div>
  {displayLayout !== 'all' && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Rotate every</span>
      <input type="number" min={2} style={{ ...F, width: 70 }}
        value={displayRotate}
        onChange={e => { const v = Math.max(2, Number(e.target.value) || 8); setDisplayRotate(v); localStorage.setItem('display_rotate', String(v)); }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>seconds</span>
    </div>
  )}
  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
    "All in One" shows every counter at once (current behavior). Otherwise the display screen rotates through groups automatically.
  </div>
</div>

            <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12, color: '#64748b' }}>
  <div style={{ fontWeight: 700, marginBottom: 8 }}>
    📺 Waiting Room Display URLs
    {outletId && (
      <span style={{ marginLeft: 6, fontWeight: 600, color: '#6366f1' }}>
        ({outlets.find(o => o.id === outletId)?.name || `Outlet ${outletId}`})
      </span>
    )}
  </div>
  {!displayToken ? (
    <div style={{ fontSize: 11, color: '#94a3b8' }}>Loading display token...</div>
  ) : (
    <>
      {(() => {
        const langs = ttsLangs.filter(Boolean).join(',');
        const layoutQ = displayLayout !== 'all' ? `&layout=${displayLayout}&rotate=${displayRotate}` : '';
        const commonQ = `${outletId ? `&outlet_id=${outletId}` : ''}${langs ? `&langs=${langs}` : ''}&gender=${ttsGender}${layoutQ}`;
        const buildUrl = (counterCode?: string) =>
          `${window.location.origin}/queue/display?token=${displayToken}${counterCode ? `&counter=${counterCode}` : ''}${commonQ}`;
        const copy = (url: string) => { navigator.clipboard.writeText(url); alert('Copied! Open this URL on your display screen.'); };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* All counters on one screen */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>All {queueData?.counter_label || 'Counter'}s</div>
                <div style={{ fontSize: 9, color: '#6366f1', wordBreak: 'break-all', fontFamily: 'monospace' }}>{buildUrl()}</div>
              </div>
              <button onClick={() => copy(buildUrl())}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e0e7ff', background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                Copy
              </button>
            </div>

            {/* One link per counter */}
            {counters.map(c => (
              <div key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>{c.name}</div>
                  <div style={{ fontSize: 9, color: '#6366f1', wordBreak: 'break-all', fontFamily: 'monospace' }}>{buildUrl(c.code)}</div>
                </div>
                <button onClick={() => copy(buildUrl(c.code))}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e0e7ff', background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                  Copy
                </button>
              </div>
            ))}
          </div>
        );
      })()}
    </>
  )}
</div>
          {/* Custom Announcement */}
<div style={{ padding: '14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
  <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 8 }}>📢 Custom Announcement</div>
  <textarea
    value={customAnnounceText}
    onChange={e => setCustomAnnounceText(e.target.value)}
    placeholder="Type anything to announce..."
    rows={3}
    style={{ ...F, resize: 'vertical', marginBottom: 8 }}
  />
  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
    <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
  <button
    onClick={() => setCustomAnnounceGender('female')}
    style={{
      flex: 1, padding: '7px', borderRadius: 8, border: '1.5px solid',
      borderColor: customAnnounceGender === 'female' ? '#ec4899' : '#e2e8f0',
      background: customAnnounceGender === 'female' ? '#fdf2f8' : '#fff',
      color: customAnnounceGender === 'female' ? '#ec4899' : '#94a3b8',
      fontWeight: 700, fontSize: 11, cursor: 'pointer'
    }}>
    👩 Female
  </button>
  <button
    onClick={() => setCustomAnnounceGender('male')}
    style={{
      flex: 1, padding: '7px', borderRadius: 8, border: '1.5px solid',
      borderColor: customAnnounceGender === 'male' ? '#6366f1' : '#e2e8f0',
      background: customAnnounceGender === 'male' ? '#eef2ff' : '#fff',
      color: customAnnounceGender === 'male' ? '#6366f1' : '#94a3b8',
      fontWeight: 700, fontSize: 11, cursor: 'pointer'
    }}>
    👨 Male
  </button>
</div>

    <select value={customAnnounceLang} onChange={e => setCustomAnnounceLang(e.target.value)} style={{ ...F, flex: 1 }}>
      <option value="en">English</option>
      <option value="hi">Hindi</option>
      <option value="pa">Punjabi</option>
      <option value="ur">Urdu</option>
      <option value="ta">Tamil</option>
      <option value="te">Telugu</option>
      <option value="kn">Kannada</option>
      <option value="ml">Malayalam</option>
      <option value="mr">Marathi</option>
      <option value="gu">Gujarati</option>
      <option value="bn">Bengali</option>
      <option value="ar">Arabic</option>
      <option value="he">Hebrew</option>
      <option value="fa">Persian</option>
      <option value="fr">French</option>
      <option value="es">Spanish</option>
      <option value="de">German</option>
      <option value="it">Italian</option>
      <option value="ru">Russian</option>
      <option value="zh-CN">Chinese</option>
      <option value="ja">Japanese</option>
      <option value="ko">Korean</option>
      <option value="tr">Turkish</option>
      <option value="vi">Vietnamese</option>
      <option value="th">Thai</option>
      <option value="id">Indonesian</option>
      <option value="ms">Malay</option>
      <option value="sw">Swahili</option>
      <option value="fil">Filipino</option>
    </select>
    <button
      onClick={async () => {
        if (!customAnnounceText.trim() || customAnnouncing) return;
        setCustomAnnouncing(true);

        // Declare OUTSIDE the try block so catch can access it
        let textToSpeak = customAnnounceText;

        try {
          if (customAnnounceLang !== 'en') {
            try {
              const tr = await fetch(`${BASE}/translate?text=${encodeURIComponent(customAnnounceText)}&tl=${customAnnounceLang}`, { headers: authHeaders() });
              const td = await tr.json();
              if (td.translated) textToSpeak = td.translated;
            } catch {}
          }
          await fetch(`${BASE}/queue/announce`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ text: textToSpeak, lang: customAnnounceLang, gender: customAnnounceGender, org_id: String(authUser?.org_id ?? authUser?.id ?? ''), outlet_id: outletId || null }),
          }).catch(() => {});

          const r = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(textToSpeak)}&lang=${customAnnounceLang}&gender=${customAnnounceGender}`);
          if (!r.ok) throw new Error('Server TTS failed');

          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);

          audio.onended = () => {
            URL.revokeObjectURL(url);
            setCustomAnnouncing(false);
          };

          audio.onerror = async () => {
            URL.revokeObjectURL(url);
            await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
            setCustomAnnouncing(false);
          };

          await audio.play().catch(async () => {
            URL.revokeObjectURL(url);
            await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
            setCustomAnnouncing(false);
          });
        } catch {
          await speakWithBrowser(textToSpeak, customAnnounceLang, customAnnounceGender);
          setCustomAnnouncing(false);
        }
      }}
      disabled={customAnnouncing || !customAnnounceText.trim()}
      style={{
        padding: '8px 14px', borderRadius: 8, border: 'none',
        background: customAnnouncing || !customAnnounceText.trim() ? '#e2e8f0' : '#6366f1',
        color: customAnnouncing || !customAnnounceText.trim() ? '#94a3b8' : '#fff',
        fontWeight: 700, fontSize: 12, cursor: customAnnounceText.trim() ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {customAnnouncing ? '🔊...' : '🔊 Speak'}
    </button>
    <button
      onClick={async () => {
        if (!customAnnounceText.trim()) return;
        const name = prompt('Save as (give it a name):');
        if (!name?.trim()) return;
        try {
          const r = await fetch(`${BASE}/queue/announcements`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ name: name.trim(), text: customAnnounceText, lang: customAnnounceLang, gender: customAnnounceGender, outlet_id: outletId || null }),
          });
          if (r.ok) fetchAnnouncements();
        } catch {}
      }}
      disabled={!customAnnounceText.trim()}
      style={{
        padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
        background: customAnnounceText.trim() ? '#f0fdf4' : '#f8fafc',
        color: customAnnounceText.trim() ? '#16a34a' : '#94a3b8',
        fontWeight: 700, fontSize: 12, cursor: customAnnounceText.trim() ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      💾 Save
    </button>
  </div>

  {/* Saved announcements list */}
  {savedAnnouncements.length > 0 && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Saved</div>
      {savedAnnouncements.map((s: any) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{s.name}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.text}</div>
          </div>
          <span style={{ fontSize: 9, background: '#eef2ff', color: '#6366f1', borderRadius: 99, padding: '2px 6px', fontWeight: 700, flexShrink: 0 }}>{s.lang}</span>
          <button
            onClick={async () => {
  setCustomAnnouncing(true);

  // 1. Declare OUTSIDE try so catch can see it:
  let textToSpeak = s.text;

  try {
    if (s.lang !== 'en') {
      try {
        const tr = await fetch(`${BASE}/translate?text=${encodeURIComponent(s.text)}&tl=${s.lang}`, { headers: authHeaders() });
        const td = await tr.json();
        if (td.translated) textToSpeak = td.translated;
      } catch {}
    }
    await fetch(`${BASE}/queue/announce`, {
  method: 'POST',
  headers: authHeaders(),
  body: JSON.stringify({ text: textToSpeak, lang: s.lang, gender: s.gender || 'female', org_id: String(authUser?.org_id ?? authUser?.id ?? ''), outlet_id: outletId || null }),
}).catch(() => {});
    const r = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(textToSpeak)}&lang=${s.lang}&gender=${s.gender || 'female'}`);
                if (!r.ok) throw new Error('Server TTS failed');
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);
                audio.onended = () => { URL.revokeObjectURL(url); setCustomAnnouncing(false); };
                audio.onerror = async () => {
                  URL.revokeObjectURL(url);
                  await speakWithBrowser(textToSpeak, s.lang, s.gender || 'female');
                  setCustomAnnouncing(false);
                };
                await audio.play().catch(async () => {
                  URL.revokeObjectURL(url);
                  await speakWithBrowser(textToSpeak, s.lang, s.gender || 'female');
                  setCustomAnnouncing(false);
                });
              } catch {
                await speakWithBrowser(textToSpeak, s.lang, s.gender || 'female');
                setCustomAnnouncing(false);
              }
            }}
            style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
          >
            🔊
          </button>
          <button
            onClick={async () => {
              try {
                await fetch(`${BASE}/queue/announcements/${s.id}`, { method: 'DELETE', headers: authHeaders() });
                fetchAnnouncements();
              } catch {}
            }}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 700, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
          >
            🗑
          </button>
        </div>
      ))}
    </div>
  )}
</div>

<button onClick={saveSettings} style={{ padding: '11px', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
  Save Settings
</button>
          </div>
        </ModalWrap>
      )}
{showTimePicker && (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(2px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div style={{ background: '#fff', borderRadius: 24, padding: 0, width: '100%', maxWidth: 480, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>

      {/* Header — dark digital readout, like a real clock */}
      <div style={{ background: 'linear-gradient(150deg,#0f172a,#1e293b)', padding: '20px 22px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Select Time</span>
          <button onClick={() => setShowTimePicker(false)}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, width: 26, height: 26, cursor: 'pointer', fontSize: 15, color: '#cbd5e1', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div onClick={() => setClockStep('hour')} style={{
            fontSize: 44, fontWeight: 800, cursor: 'pointer', padding: '2px 8px', borderRadius: 10,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            color: clockStep === 'hour' ? '#818cf8' : '#f1f5f9',
            background: clockStep === 'hour' ? 'rgba(99,102,241,0.15)' : 'transparent',
            transition: 'all 0.15s',
          }}>{String(clockHour).padStart(2, '0')}</div>
          <div style={{ fontSize: 40, fontWeight: 300, color: '#475569', lineHeight: 1 }}>:</div>
          <div onClick={() => setClockStep('minute')} style={{
            fontSize: 44, fontWeight: 800, cursor: 'pointer', padding: '2px 8px', borderRadius: 10,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            color: clockStep === 'minute' ? '#818cf8' : '#f1f5f9',
            background: clockStep === 'minute' ? 'rgba(99,102,241,0.15)' : 'transparent',
            transition: 'all 0.15s',
          }}>{String(clockMinute).padStart(2, '0')}</div>

          {/* AM/PM segmented control */}
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
            {(['AM', 'PM'] as const).map(p => (
              <button key={p} onClick={() => setClockPeriod(p)} style={{
                padding: '5px 12px', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 800, letterSpacing: '0.02em',
                background: clockPeriod === p ? '#6366f1' : 'transparent',
                color: clockPeriod === p ? '#fff' : '#64748b',
                transition: 'all 0.15s',
              }}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: 22 }}>
        {/* Analog dial */}
        <div style={{ position: 'relative', width: 252, height: 252, margin: '4px auto 22px' }}>
          <svg width="252" height="252" style={{ position: 'absolute', top: 0, left: 0 }}>
            <defs>
              <radialGradient id="dialFace" cx="50%" cy="42%" r="70%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#f1f5f9" />
              </radialGradient>
            </defs>
            <circle cx={126} cy={126} r={124} fill="url(#dialFace)" stroke="#e2e8f0" strokeWidth={1} />

            {/* Minute tick marks (only meaningful in minute step, subtle otherwise) */}
            {Array.from({ length: 60 }).map((_, i) => {
              if (i % 5 === 0) return null; // skip where numerals sit
              const angle = (i * 6) - 90;
              const rad = angle * Math.PI / 180;
              const rOuter = 112, rInner = 105;
              return (
                <line key={i}
                  x1={126 + rOuter * Math.cos(rad)} y1={126 + rOuter * Math.sin(rad)}
                  x2={126 + rInner * Math.cos(rad)} y2={126 + rInner * Math.sin(rad)}
                  stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" />
              );
            })}

            {/* Clock hand */}
            {(() => {
              const selectedIndex = clockStep === 'hour' ? (clockHour % 12) : (Math.round(clockMinute / 5) % 12);
              const angle = (selectedIndex * 30) - 90;
              const rad = angle * Math.PI / 180;
              const handLength = 88;
              const x2 = 126 + handLength * Math.cos(rad);
              const y2 = 126 + handLength * Math.sin(rad);
              return (
                <g>
                  <line x1={126} y1={126} x2={x2} y2={y2} stroke="#6366f1" strokeWidth={2.5} strokeLinecap="round" />
                  <circle cx={x2} cy={y2} r={17} fill="#6366f1" opacity={0.12} />
                  <circle cx={126} cy={126} r={4} fill="#6366f1" />
                </g>
              );
            })()}
          </svg>

          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30) - 90;
            const rad = angle * Math.PI / 180;
            const r = 96;
            const x = 126 + r * Math.cos(rad);
            const y = 126 + r * Math.sin(rad);
            const label = clockStep === 'hour' ? (i === 0 ? 12 : i) : String(i * 5).padStart(2, '0');
            const isSelected = clockStep === 'hour' ? (clockHour % 12) === i : Math.round(clockMinute / 5) % 12 === i;
            return (
              <div key={i}
                onClick={() => {
                  if (clockStep === 'hour') {
                    setClockHour(i === 0 ? 12 : i);
                    setClockStep('minute');
                  } else {
                    setClockMinute(i * 5);
                  }
                }}
                style={{
                  position: 'absolute', left: x, top: y, transform: 'translate(-50%,-50%)',
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', zIndex: 4,
                  fontVariantNumeric: 'tabular-nums',
                  background: isSelected ? '#6366f1' : 'transparent',
                  color: isSelected ? '#fff' : '#334155',
                  boxShadow: isSelected ? '0 3px 10px rgba(99,102,241,0.4)' : 'none',
                  transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
                }}>
                {label}
              </div>
            );
          })}
        </div>

        {/* Quick select chips */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Quick Select</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {[
              '08:00', '09:00', '10:00', '11:00',
              '12:00', '13:00', '14:00', '15:00',
              '16:00', '17:00', '18:00', '19:00',
              '20:00', '21:00', '22:00',
            ].map(tm => {
              const [h, m] = tm.split(':'); const hour = parseInt(h);
              const ampm: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM'; const h12 = hour % 12 || 12;
              const selected = clockHour === h12 && clockMinute === parseInt(m) && clockPeriod === ampm;
              return (
                <button key={tm} onClick={() => { setClockHour(h12); setClockMinute(parseInt(m)); setClockPeriod(ampm); }}
                  style={{
                    padding: '7px 4px', borderRadius: 8, border: `1.5px solid ${selected ? '#6366f1' : '#e2e8f0'}`,
                    background: selected ? '#eef2ff' : '#fff', color: selected ? '#4f46e5' : '#64748b',
                    fontWeight: 700, fontSize: 11, cursor: 'pointer', transition: 'all 0.15s',
                    textAlign: 'center',
                  }}>
                  {`${String(h12).padStart(2, '0')}:${m} ${ampm}`}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom time */}
        <div style={{ marginBottom: 18, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Custom Time</div>
          <div style={{ position: 'relative' }}>
            <input
              ref={customTimeInputRef}
              type="time"
              value={(() => {
                let h24 = clockHour % 12;
                if (clockPeriod === 'PM') h24 += 12;
                return `${String(h24).padStart(2, '0')}:${String(clockMinute).padStart(2, '0')}`;
              })()}
              onChange={e => {
                const [hh, mm] = e.target.value.split(':').map(Number);
                if (isNaN(hh) || isNaN(mm)) return;
                const period: 'AM' | 'PM' = hh >= 12 ? 'PM' : 'AM';
                setClockHour(hh % 12 || 12);
                setClockMinute(mm);
                setClockPeriod(period);
              }}
              style={{
                width: '100%', padding: '11px 40px 11px 14px', borderRadius: 12,
                border: '1.5px solid #e2e8f0', fontSize: 15, fontWeight: 700,
                color: '#0f172a', outline: 'none', boxSizing: 'border-box',
                fontFamily: 'inherit', background: '#f8fafc',
              }}
            />

          </div>
        </div>

        <button onClick={() => {
          let h24 = clockHour % 12;
          if (clockPeriod === 'PM') h24 += 12;
          setForm(p => ({ ...p, appointment_time: `${String(h24).padStart(2, '0')}:${String(clockMinute).padStart(2, '0')}` }));
          setShowTimePicker(false);
        }}
          style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 6px 16px rgba(99,102,241,0.35)', letterSpacing: '0.01em' }}>
          Confirm Time
        </button>
      </div>
    </div>
  </div>
)}
      {editingToken && (
  <ModalWrap title={`✏️ Edit Token #${String(editingToken.token_number).padStart(3,'0')} — ${editingToken.customer_name}`} onClose={() => setEditingToken(null)} width={520}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={L}>Name</label>
        <input style={F} value={editForm.customer_name || ''} onChange={e => setEditForm(p => ({ ...p, customer_name: e.target.value }))} />
      </div>
      <div>
        <label style={L}>Phone</label>
        <PhoneInput
          value={editForm.customer_phone || ''}
          onChange={v => setEditForm(p => ({ ...p, customer_phone: v }))}
          inputStyle={F}
        />
      </div>
      <div>
        <label style={L}>Email</label>
        <input style={F} type="email" value={editForm.customer_email || ''} onChange={e => setEditForm(p => ({ ...p, customer_email: e.target.value }))} />
      </div>
      {counters.length > 0 && (
        <div>
          <label style={L}>{queueData?.counter_label || 'Counter'}</label>
          <select style={F} value={editForm.counter_code || ''} onChange={e => setEditForm(p => ({ ...p, counter_code: e.target.value }))}>
            <option value="">— Any —</option>
            {counters.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div>
        <label style={L}>{queueData?.service_label || 'Service'}</label>
        <input style={F} value={editForm.service || ''} onChange={e => setEditForm(p => ({ ...p, service: e.target.value }))} />
      </div>
      <div>
        <label style={L}>Time Slot</label>
        <input style={F} placeholder="e.g. 09:30 AM" value={editForm.appointment_time || ''} onChange={e => setEditForm(p => ({ ...p, appointment_time: e.target.value }))} />
      </div>
      <div>
        <label style={L}>Notes</label>
        <textarea style={{ ...F, height: 70, resize: 'vertical' }} value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={() => setEditingToken(null)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
        <button
          disabled={editSaving}
          onClick={async () => {
            const rawEditPhone = editForm.customer_phone ? editForm.customer_phone.trim() : '';
            const cleanEditPhone = rawEditPhone ? getCleanE164Phone(rawEditPhone) : '';

            if (rawEditPhone && !cleanEditPhone) {
              alert('Please enter a valid phone number (between 7 and 15 digits).');
              return;
            }

            setEditSaving(true);
            try {
              const toSend = {
                ...editForm,
                customer_phone: cleanEditPhone || null,
              };
              const r = await fetch(`${BASE}/queue/tokens/${editingToken.id}`, {
                method: 'PATCH', headers: authHeaders(),
                body: JSON.stringify(toSend),
              });
              if (!r.ok) { alert(await r.text()); return; }
              fetchQueue();
              setEditingToken(null);
            } catch (e) { alert(`Error: ${e}`); }
            setEditSaving(false);
          }}
          style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: editSaving ? '#e2e8f0' : '#6366f1', color: editSaving ? '#94a3b8' : '#fff', fontWeight: 700, fontSize: 13, cursor: editSaving ? 'not-allowed' : 'pointer' }}>
          {editSaving ? 'Saving...' : '✅ Save Changes'}
        </button>
      </div>
    </div>
  </ModalWrap>
)}

<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
    </>
  );
}
